import axios from "axios";
import { attachDataScopeToAxiosConfig } from "../rbac/dataAccessScope";
import {
  clearAuthSession,
  getAuthItem,
  getAuthJson,
  isEssProxySession,
  setAuthItem,
  setAuthJson,
} from "../utils/authStorage";
import { flattenEmployeesInResponse } from "../utils/displayLabel";

// In development use same-origin /api (proxied by craco). Production uses full URL.
export const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "/api/"
    : "https://admin.payceey.com/api/");

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  // 🚀 Browser automatically handles compression and caching
  // DO NOT manually set Accept-Encoding - browsers reject this as unsafe
  // The browser will automatically send gzip/deflate/br compression requests
  headers: {
    // Safe custom headers only - no compression headers
  },
  timeout: 60000,
});

// Add request interceptor to add auth token and frontend host
api.interceptors.request.use(
  (config) => {
    const token = getAuthItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add frontend host to headers for domain validation
    const frontendHost = window.location.host;
    config.headers["X-Frontend-Host"] = frontendHost;

    attachDataScopeToAxiosConfig(config);

    // Debug: Log outgoing request details (method + URL + params/data summary)
    try {
      const method = (config.method || "GET").toUpperCase();
      const fullUrl = `${config.baseURL || ""}${config.url || ""}`;
      // Avoid logging sensitive tokens; log params and small payloads only
      const safeData =
        config.data && typeof config.data === "object"
          ? {
              ...(config.data instanceof FormData
                ? { formData: true }
                : config.data),
            }
          : config.data;
      console.debug("API Request →", method, fullUrl, {
        params: config.params,
        data: safeData,
      });
    } catch (logErr) {
      // swallow logging errors
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => {
    // If the request was successful, return the response
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Don't handle 401 for login/auth endpoints to avoid redirect loops
      const isAuthEndpoint =
        error.config?.url?.includes("/auth/login") ||
        error.config?.url?.includes("/auth/forgot-password");
      const isPublicHiringProfile = error.config?.url?.includes(
        "/hiring/public/profile/"
      );

      const business401 =
        /Employee profile not found/i.test(
          String(error.response?.data?.message || "")
        );
      if (!isAuthEndpoint && !isPublicHiringProfile && !business401) {
        // Clear only the active session store (proxy sessionStorage vs admin localStorage)
        clearAuthSession();

        // Only redirect if we're not already on the login page
        // This prevents redirect loops
        if (window.location.pathname !== "/login") {
          // Use window.location for a hard redirect to ensure state is cleared
          window.location.href = "/login";
        }
      }
    }

    // Additional debug for failed responses
    try {
      console.error("API Response Error ←", {
        url: error.config?.url,
        method: (error.config?.method || "").toUpperCase(),
        status: error.response?.status,
        responseData: error.response?.data,
      });
    } catch (logErr) {
      // ignore
    }

    try {
      const responseData = error.response?.data;
      const backendMessage =
        (responseData && typeof responseData === "object" && responseData.message) ||
        (typeof responseData === "string" ? responseData : null);
      if (backendMessage) {
        error.message = backendMessage;
      }
    } catch (e) {
      // ignore
    }

    // Return the error so it can be handled by the calling code
    return Promise.reject(error);
  }
);

// Company services
export const companyService = {
  getCompanyData: async (companyId, { skipCache = false } = {}) => {
    try {
      const response = await api.get(`/companies/${companyId}`, {
        params: skipCache ? { skipCache: true } : {},
        headers: skipCache ? { "x-skip-cache": "1" } : {},
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateCompanyData: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMinimumWagesSetting: async (companyId) => {
    try {
      const response = await api.get(
        `/companies/${companyId}/minimum-wages-setting`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateMinimumWagesSetting: async (companyId, payload) => {
    try {
      const response = await api.put(
        `/companies/${companyId}/minimum-wages-setting`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // 🆕 Refresh company data from backend and update cache
  refreshCompanyData: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      if (response.data.success || response.data.company) {
        const companyData = response.data.company || response.data;
        setAuthJson("companyData", companyData);

        // Notify all components listening for this event
        window.dispatchEvent(
          new CustomEvent("companyDataUpdated", {
            detail: companyData,
            timestamp: new Date(),
          })
        );

        console.log("✅ Company data refreshed:", companyData);
        return companyData;
      }
    } catch (error) {
      console.error("❌ Failed to refresh company data:", error);
      throw error;
    }
  },

  getCompanyByDomain: async () => {
    try {
      const response = await api.get("/companies/test-domain");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Auth services
export const authService = {
  login: async (email, password, userType = null) => {
    try {
      const basePayload = { email, password };
      // Backend resolves admin vs employee (password-aware). Only send userType
      // when the caller explicitly requests a role.
      const payload = userType
        ? { ...basePayload, userType }
        : basePayload;
      const response = await api.post("/auth/login", payload);

      if (response.data.success) {
        setAuthItem("token", response.data.token);
        setAuthJson("currentUser", response.data.user);

        // Seed the RBAC snapshot so the first authenticated render already has
        // permissions without waiting for the /me/permissions round-trip.
        try {
          const { persistRbacFromLogin, fetchAndPersistMyPermissions } = await import(
            "../rbac/PermissionsContext"
          );
          persistRbacFromLogin({
            user: response.data.user,
            userType: response.data.userType,
            rbac: response.data.rbac,
          });
          // Authoritative sync must happen immediately after login so all
          // role-based gates are consistent before user starts navigating.
          await fetchAndPersistMyPermissions();
        } catch (rbacErr) {
          console.warn("[RBAC] failed to seed snapshot at login:", rbacErr?.message || rbacErr);
        }

        // Fetch and store company data on successful login
        if (response.data.user && response.data.user.companyId) {
          try {
            const companyResponse = await companyService.getCompanyData(
              response.data.user.companyId
            );
            console.log("AuthService - Company Response:", companyResponse);
            if (companyResponse.success) {
              setAuthJson("companyData", companyResponse.company);
              console.log(
                "AuthService - Stored company data:",
                companyResponse.company
              );
            }
          } catch (companyError) {
            console.error("Failed to fetch company data:", companyError);
            // Fallback: use company data from login response if available
            if (response.data.company) {
              setAuthJson("companyData", response.data.company);
              console.log(
                "AuthService - Fallback: stored company data from login response:",
                response.data.company
              );
            }
          }
        }
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Proxy login from admin to employee context (ESS access).
  proxyLoginAsEmployee: async ({ employeeId, employeeUserId = null }) => {
    const endpoints = [
      "/auth/access-ess",
      "/auth/proxy-login",
      "/auth/employee-proxy-login",
    ];
    const idsToTry = [employeeId, employeeUserId].filter(Boolean);
    const payloads = idsToTry.flatMap((id) => [
      { employeeId: id },
      { id },
      { userId: id },
      { employeeUserId: id },
      { targetEmployeeId: id },
      { employee: { id } },
      { employee: { employeeId: id } },
    ]);
    let lastError = null;

    const pathEndpoints = idsToTry.flatMap((id) => [
      `/auth/access-ess/${id}`,
      `/auth/proxy-login/${id}`,
      `/auth/employee-proxy-login/${id}`,
      `/auth/access-ess/user/${id}`,
      `/auth/proxy-login/user/${id}`,
    ]);

    const normalizeAuthResponse = (data) => {
      const hasUsableSession = !!(
        data?.token &&
        (data?.user || data?.employee || data?.data?.user || data?.data?.employee)
      );
      if (!(data?.success || hasUsableSession)) return null;
      if (data?.data && (data?.data?.user || data?.data?.employee || data?.data?.token)) {
        return {
          ...data,
          token: data.token || data.data.token,
          user: data.user || data.employee || data.data.user || data.data.employee,
          employee: data.employee || data.data.employee,
        };
      }
      return data;
    };

    for (const endpoint of endpoints) {
      for (const payload of payloads) {
        try {
          const response = await api.post(endpoint, payload);
          const data = response?.data;
          const normalized = normalizeAuthResponse(data);
          if (normalized) return normalized;
          lastError = data || { message: "Proxy login failed" };
        } catch (error) {
          const status = error?.response?.status;
          // Keep trying with GET for unsupported/invalid POST combinations.
          if (status === 400 || status === 404 || status === 405 || status === 422) {
            lastError = error;
          } else {
            throw error?.response?.data || error?.message || error;
          }
        }

        try {
          const response = await api.get(endpoint, { params: payload });
          const data = response?.data;
          const normalized = normalizeAuthResponse(data);
          if (normalized) return normalized;
          throw data || { message: "Proxy login failed" };
        } catch (error) {
          const status = error?.response?.status;
          if (status === 400 || status === 404 || status === 405 || status === 422) {
            lastError = error;
            continue;
          }
          throw error?.response?.data || error?.message || error;
        }
      }
    }

    for (const endpoint of pathEndpoints) {
      try {
        const postResp = await api.post(endpoint);
        const normalizedPost = normalizeAuthResponse(postResp?.data);
        if (normalizedPost) return normalizedPost;
      } catch (error) {
        const status = error?.response?.status;
        if (![400, 404, 405, 422].includes(status)) {
          throw error?.response?.data || error?.message || error;
        }
        lastError = error;
      }
      try {
        const getResp = await api.get(endpoint);
        const normalizedGet = normalizeAuthResponse(getResp?.data);
        if (normalizedGet) return normalizedGet;
      } catch (error) {
        const status = error?.response?.status;
        if (![400, 404, 405, 422].includes(status)) {
          throw error?.response?.data || error?.message || error;
        }
        lastError = error;
      }
    }

    throw (
      lastError?.response?.data?.message ||
      (typeof lastError?.response?.data === "string"
        ? "Access ESS API endpoint is not configured on backend."
        : lastError?.response?.data) ||
      lastError?.message ||
      "Access ESS endpoint is not available"
    );
  },

  forgotPassword: async (email, userType = null) => {
    try {
      const payload = { email };
      if (userType) payload.userType = userType;
      const response = await api.post("/auth/forgot-password", payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  logout: () => {
    const proxy = isEssProxySession();
    clearAuthSession();
    // Clear the entire proxy session when logging out from an ESS proxy window.
    if (proxy && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("essProxySession");
    }
  },

  // Issue a 2-minute one-time proxy token for the given employee (admin only).
  issueEssProxyToken: async (employeeId) => {
    const response = await api.post("/auth/ess-token", { employeeId });
    if (!response.data?.success || !response.data?.proxyToken) {
      throw new Error(response.data?.message || "Failed to issue proxy token");
    }
    return response.data.proxyToken;
  },

  // Redeem a one-time proxy token — called from the new ESS window.
  // Uses raw axios so the admin's session token is NOT sent in this request.
  redeemEssProxyToken: async (token) => {
    const response = await axios.post(
      `${API_URL}auth/ess-redeem`,
      { token },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Frontend-Host": typeof window !== "undefined" ? window.location.host : "",
        },
      }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Token redemption failed");
    }
    return response.data;
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Employee services
export const employeeService = {
  createEmployee: async (employeeData) => {
    const response = await api.post("/employees", employeeData);
    return response.data;
  },
  validateEmployeeImport: async (formData, section = "org") => {
    if (section && section !== "org") {
      formData.append("section", section);
    }
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },

  downloadImportTemplate: async (section = "org") => {
    const response = await api.get(`/employees/import/template/${section}`, {
      responseType: "blob",
    });
    return response;
  },

  importSectionRecords: async (section, validRecords) => {
    const response = await api.post(
      "/employees/import/section",
      { section, validRecords },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  },

  importPersonalDetails: async (formData) => {
    formData.append("section", "personal");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importFamilyDetails: async (formData) => {
    formData.append("section", "family");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importProfessionalDetails: async (formData) => {
    formData.append("section", "professional");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importBankDetails: async (formData) => {
    formData.append("section", "bank");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importQualificationDetails: async (formData) => {
    formData.append("section", "qualification");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importExperienceDetails: async (formData) => {
    formData.append("section", "professional");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },
  importStatutoryDetails: async (formData) => {
    formData.append("section", "statutory");
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },

  // Add this new function
  importConfirmedEmployees: async (validRecords, options = {}) => {
    const { onUploadProgress } = options;
    const response = await api.post(
      "/employees/import/confirmed",
      validRecords,
      {
        onUploadProgress,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },

  downloadBulkExitImportTemplate: async () => {
    const response = await api.get("/employees/import/bulk-exit/template", {
      responseType: "blob",
    });
    return response;
  },

  validateBulkExitImport: async (formData) => {
    const response = await api.post(
      "/employees/import/bulk-exit/validate",
      formData
    );
    return response.data;
  },

  confirmBulkExitImport: async (validRecords) => {
    const response = await api.post(
      "/employees/import/bulk-exit/confirmed",
      validRecords,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return response.data;
  },

  uploadEmployeePhoto: async (employeeId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("employeeId", employeeId);

      const response = await api.post("/employees/upload-photo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadEmployeeFamilyPhoto: async (employeeId, familyMemberId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("employeeId", employeeId);
      formData.append("familyMemberId", familyMemberId);

      const response = await api.post(
        "/employees/upload-family-photo",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getEmployeePhotoUrl: async (employeeId) => {
    const response = await api.get(`/employees/${employeeId}/photo-url`);
    return response.data;
  },

  getEmployeePhotoDownloadUrl: async (employeeId) => {
    const response = await api.get(`/employees/${employeeId}/photo-download-url`);
    return response.data;
  },

  // Download employee photo as blob (proxied through backend to avoid CORS)
  getEmployeePhotoBlob: async (employeeId) => {
    const response = await api.get(
      `/employees/${employeeId}/photo-download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Download employee photo as blob with downloadFileName (proxied through backend to avoid CORS)
  getEmployeePhotoBlobWithFilename: async (employeeId) => {
    const response = await api.get(
      `/employees/${employeeId}/photo-download`,
      { responseType: 'blob' }
    );
    const downloadFileName = response.headers['x-download-filename'] || response.headers['X-Download-Filename'] || '';
    return {
      blob: response.data,
      downloadFileName: downloadFileName
    };
  },

  getFamilyMemberPhotoUrl: async (employeeId, familyMemberId) => {
    const response = await api.get(
      `/employees/${employeeId}/family-members/${familyMemberId}/photo-url`
    );
    return response.data;
  },

  // Download family member photo as blob (proxied through backend to avoid CORS)
  getFamilyMemberPhotoBlob: async (employeeId, familyMemberId) => {
    const response = await api.get(
      `/employees/${employeeId}/family-members/${familyMemberId}/photo-download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  getEmployeeDocumentUrl: async (documentId) => {
    const response = await api.get(`/employees/documents/${documentId}/url`);
    return response.data;
  },

  getEmployeeDocumentDownloadUrl: async (documentId) => {
    const response = await api.get(`/employees/documents/${documentId}/download-url`);
    return response.data;
  },

  getEmployees: async (params = {}, axiosConfig = {}) => {
    // Add timestamp to prevent caching
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees", {
      params: queryParams,
      ...axiosConfig,
    });
    return flattenEmployeesInResponse(response.data);
  },

  /** Exact-match duplicate check across all employment statuses (official/personal email, PAN, Aadhaar, mobile). */
  checkEmployeeIdentityDuplicates: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/identity-duplicate-check", {
      params: queryParams,
    });
    return response.data;
  },

  getEmployeeCounts: async (params = {}) => {
    // Get employee counts by status with filters (excludes pagination params)
    const { page, limit, ...filterParams } = params;
    const queryParams = { ...filterParams, _t: Date.now() };
    const response = await api.get("/employees/counts", { params: queryParams });
    return response.data;
  },

  getEmployeesWithQualifications: async () => {
    // Try different approaches to get employees with qualifications
    try {
      // First try with include parameter
      const response = await api.get("/employees", {
        params: { include: "qualifications,experiences,family" },
      });
      return response.data;
    } catch (error) {
      try {
        // Try with relations parameter
        const response = await api.get("/employees", {
          params: { relations: "qualifications,experiences,family" },
        });
        return response.data;
      } catch (error2) {
        try {
          // Try with populate parameter
          const response = await api.get("/employees", {
            params: { populate: "qualifications,experiences,family" },
          });
          return response.data;
        } catch (error3) {
          try {
            // Try with with parameter
            const response = await api.get("/employees", {
              params: { with: "qualifications,experiences,family" },
            });
            return response.data;
          } catch (error4) {
            // If all fail, try the basic endpoint with expanded parameter
            const response = await api.get("/employees", {
              params: { expanded: "true" },
            });
            return response.data;
          }
        }
      }
    }
  },

  getRelievedEmployees: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/relieved", { params: queryParams });
    return flattenEmployeesInResponse(response.data);
  },

  getReportingEmployees: async (excludeEmployeeId = null) => {
    const params = excludeEmployeeId ? { excludeId: excludeEmployeeId } : {};
    const response = await api.get("/employees/reporting-managers", { params });
    return flattenEmployeesInResponse(response.data);
  },

  // Get employees who report to the currently-logged-in user ("My Team").
  // Optional `managerId` lets admins preview any manager's team.
  getMyReportees: async (managerId = null) => {
    const params = managerId ? { managerId } : {};
    const response = await api.get("/employees/my-reportees", { params });
    return flattenEmployeesInResponse(response.data);
  },

  getEmployee: async (id) => {
    const response = await api.get(`/employees/${id}`);
    return flattenEmployeesInResponse(response.data);
  },

  updateEmployee: async (id, employeeData) => {
    console.log("Updating employee:", employeeData);
    const response = await api.put(`/employees/${id}`, employeeData);
    return response.data;
  },

  bulkAutoRelieveEmployees: async (employeeIds = []) => {
    const response = await api.post("/employees/bulk/auto-relieve", {
      employeeIds,
    });
    return response.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  importEmployees: async (formData) => {
    const response = await api.post("/employees/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Export all employee data as Excel from the backend.
   * Params match getEmployees (sortBy, sortOrder, search, filters, limit, etc.).
   * Frontend sends sortBy: "dateOfJoin", sortOrder: "desc" so backend must order by
   * date of join descending (newest first), same as the employees list API.
   */
  exportEmployees: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/export", {
      params: queryParams,
      responseType: "blob",
    });
    return response;
  },

  /** Get employees report summary (count) with same filters as export - for Reports > Employees Report. */
  getEmployeesReportSummary: async (params = {}) => {
    const response = await api.get("/employees/reports/employees/summary", { params });
    return response;
  },

  /** Get confirmation report summary (count) for selected month - matches export count. */
  getConfirmationReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/confirmation/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated confirmation report Excel for selected month (DOJ desc). */
  exportConfirmationReport: async (month, year) => {
    const response = await api.get("/employees/reports/confirmation/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Get salary report summary (active employees count) for selected month. */
  getSalaryReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/salary/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated salary report Excel for selected month (active employees in month). */
  exportSalaryReport: async (month, year) => {
    const response = await api.get("/employees/reports/salary/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Backend-generated service report Excel (active employees only, sort by DOJ, DD/MM/YYYY, Service(Years)). */
  exportServiceReport: async (params = {}) => {
    const response = await api.get("/employees/reports/service/export", {
      params: { dateTo: params.dateTo, dateFrom: params.dateFrom, branch: params.branch, department: params.department, designation: params.designation, grade: params.grade, category: params.category, subDepartment: params.subDepartment },
      responseType: "blob",
    });
    return response;
  },

  /** Backend-generated age report Excel (active as of today, DOJ/Confirmation/DOB DD/MM/YYYY, Current Age years.decimal, sort by DOJ desc). */
  exportAgeReport: async (params = {}) => {
    const response = await api.get("/employees/reports/age/export", {
      params: { branch: params.branch, department: params.department, designation: params.designation, category: params.category },
      responseType: "blob",
    });
    return response;
  },

  /** Get resignations report summary (count) for selected month. */
  getResignationsReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/resignations/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated resignations report Excel for selected month (DOJ desc). */
  exportResignationsReport: async (month, year) => {
    const response = await api.get("/employees/reports/resignations/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Get transfers report summary (count) for selected month. */
  getTransfersReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/transfers/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated transfers report Excel for selected month (employees transferred in that month). */
  exportTransfersReport: async (month, year) => {
    const response = await api.get("/employees/reports/transfers/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  // Tab-wise export methods
  exportOrgDetails: async () => {
    const response = await api.get("/employees/export/org-details", {
      responseType: "blob",
    });
    return response;
  },

  exportPersonalDetails: async () => {
    const response = await api.get("/employees/export/personal-details", {
      responseType: "blob",
    });
    return response;
  },

  exportFamilyDetails: async () => {
    const response = await api.get("/employees/export/family-details", {
      responseType: "blob",
    });
    return response;
  },

  exportProfessionalDetails: async () => {
    const response = await api.get("/employees/export/professional-details", {
      responseType: "blob",
    });
    return response;
  },

  exportBankDetails: async () => {
    const response = await api.get("/employees/export/bank-details", {
      responseType: "blob",
    });
    return response;
  },

  exportQualificationDetails: async () => {
    const response = await api.get("/employees/export/qualification-details", {
      responseType: "blob",
    });
    return response;
  },

  exportStatutoryDetails: async () => {
    const response = await api.get("/employees/export/statutory-details", {
      responseType: "blob",
    });
    return response;
  },

  exportExperienceDetails: async () => {
    const response = await api.get("/employees/export/experience-details", {
      responseType: "blob",
    });
    return response;
  },

  uploadDocument: async (formData) => {
    try {
      const response = await api.post("/employees/upload-document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Upload document error:", error);
      // Re-throw the error so the calling function can handle it
      throw error;
    }
  },

  /** Pre-filled statutory template PDF. formType: Form-11 | Form-2 | Form-F. Returns { blob, filename }. */
  getPrefilledTemplatePdf: async (employeeId, formType) => {
    const response = await api.get(`/employees/${employeeId}/template-pdf`, {
      params: { formType },
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"];
    let filename = `Form-${formType === "Form-11" ? "11-EPF" : formType === "Form-2" ? "2-ESI" : "F-POGA"}-prefilled.pdf`;
    if (disposition && /filename="?([^";\n]+)"?/i.test(disposition)) {
      filename = disposition.match(/filename="?([^";\n]+)"?/i)[1].trim();
    }
    return { blob: response.data, filename };
  },

  // Employee payslip documents (Cloudflare path: employee-payslips/company_name/Emp_name_emp_code)
  getPayslips: async ({ monthYear }) => {
    const response = await api.get("/payroll/employee-payslips", {
      params: { monthYear },
    });
    return response.data;
  },
  getMyPayslipsForYear: async (year) => {
    const response = await api.get("/payroll/employee-payslips/my-payslips", {
      params: { year },
    });
    return response.data;
  },
  uploadPayslip: async (formData) => {
    const response = await api.post("/payroll/employee-payslips/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  // Bulk upload: FormData with monthYear and multiple files (field name "payslips")
  uploadBulkPayslips: async (formData) => {
    const response = await api.post("/payroll/employee-payslips/bulk-upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  downloadPayslip: async (employeeId, monthYear, type = "standard") => {
    const response = await api.get(
      `/payroll/employee-payslips/download/${employeeId}`,
      {
        params: { monthYear, type },
        responseType: "blob",
      }
    );
    return response.data;
  },
  // POST body: { monthYear: "November-2025", employeeIds: [id, ...] }
  // Frontend download filename: ${companyCode}-payslips-${month}-${year}.zip (e.g. pss-payslips-dec-2025.zip).
  // Backend should: find payslip PDFs for that month per employee, zip with filenames without employee name
  // (e.g. EmpCode-nov-2025.pdf), return application/zip. If none found,
  // return 200 + JSON { success: false, message: "No payslip documents found" } or 404.
  downloadBulkPayslips: async ({ monthYear, employeeIds }) => {
    const response = await api.post(
      "/payroll/employee-payslips/bulk-download",
      { monthYear, employeeIds },
      { responseType: "blob" }
    );
    return response; // full axios response so caller can use response.data
  },
  unlockPayslips: async (month, year, payload = {}) => {
    const response = await api.put(`/payroll/employee-payslips/unlock/${month}/${year}`, payload);
    return response.data;
  },
  listActionRecords: async (actionKey, params = {}) => {
    const response = await api.get(
      `/employees/actions/${encodeURIComponent(actionKey)}/records`,
      { params }
    );
    return response.data;
  },
};

// Attendance services
export const attendanceService = {
  checkIn: async (payload = {}) => {
    const response = await api.post("/attendance/check-in", payload);
    return response.data;
  },

  checkOut: async (payload = {}) => {
    const response = await api.put("/attendance/check-out", payload);
    return response.data;
  },

  getCurrentAttendanceSession: async () => {
    const response = await api.get("/attendance/current-session");
    return response.data;
  },

  runAttendanceEngine: async ({
    payMonth,
    employeeIds = [],
    skipMonthProcessMark = false,
    unprocessHeal = false,
    skipProcessStatus = false,
  } = {}) => {
    const response = await api.post(
      "/attendance/engine/run",
      {
        payMonth,
        employeeIds,
        ...(skipMonthProcessMark ? { skipMonthProcessMark: true } : {}),
        ...(unprocessHeal ? { unprocessHeal: true } : {}),
        ...(skipProcessStatus ? { skipProcessStatus: true } : {}),
      },
      { timeout: 180000 }
    );
    return response.data;
  },

  listAttendanceExceptions: async (params = {}) => {
    const response = await api.get("/attendance/engine/exceptions", { params });
    return response.data;
  },

  listOvertimeRecords: async (params = {}) => {
    const response = await api.get("/attendance/engine/overtime", { params });
    return response.data;
  },

  listCompOffLedger: async (params = {}) => {
    const response = await api.get("/attendance/engine/comp-off", { params });
    return response.data;
  },

  getCompOffBalance: async (params = {}) => {
    const response = await api.get("/attendance/engine/comp-off/balance", { params });
    return response.data;
  },

  downloadCompOffOpeningTemplate: async () => {
    const response = await api.get("/attendance/engine/comp-off/opening-import/template", {
      responseType: "blob",
    });
    return response;
  },

  importCompOffOpening: async (payload = {}) => {
    const response = await api.post("/attendance/engine/comp-off/opening-import", payload);
    return response.data;
  },

  downloadLopReversalImportTemplate: async () => {
    const response = await api.get("/attendance/lop-reversal-import/template", {
      responseType: "blob",
    });
    return response;
  },

  importLopReversal: async (payload = {}) => {
    const response = await api.post("/attendance/lop-reversal-import", payload, {
      timeout: 120000,
    });
    return response.data;
  },

  requestCompOffWork: async (payload = {}) => {
    const response = await api.post("/attendance/engine/comp-off/work", payload);
    return response.data;
  },

  updateCompOffStatus: async (id, status, extra = {}) => {
    const response = await api.put(`/attendance/engine/comp-off/${id}/status`, {
      status,
      ...extra,
    });
    return response.data;
  },

  submitOvertimeClaim: async (payload = {}) => {
    const response = await api.post("/attendance/engine/overtime", payload);
    return response.data;
  },

  updateOvertimeStatus: async (id, status, extra = {}) => {
    const response = await api.put(`/attendance/engine/overtime/${id}/status`, {
      status,
      ...extra,
    });
    return response.data;
  },

  applyRegularisedChecks: async (payload = {}) => {
    const response = await api.post("/attendance/engine/regularise/apply", payload);
    return response.data;
  },

  listEmployeeChecks: async (params = {}) => {
    const response = await api.get("/attendance/engine/checks", { params });
    return response.data;
  },

  addEmployeeCheck: async (payload = {}) => {
    const response = await api.post("/attendance/engine/checks", payload);
    return response.data;
  },

  updateEmployeeCheck: async (id, payload = {}) => {
    const response = await api.put(`/attendance/engine/checks/${id}`, payload);
    return response.data;
  },

  deleteEmployeeCheck: async (id, payload = {}) => {
    const response = await api.delete(`/attendance/engine/checks/${id}`, { data: payload });
    return response.data;
  },

  downloadChecksImportTemplate: async () => {
    const response = await api.get("/attendance/engine/checks/import/template", {
      responseType: "blob",
    });
    return response;
  },

  importEmployeeChecks: async (payload = {}) => {
    const response = await api.post("/attendance/engine/checks/import", payload);
    return response.data;
  },

  evaluateCompOffEarn: async (payload = {}) => {
    const response = await api.post("/attendance/engine/comp-off/evaluate", payload);
    return response.data;
  },

  createDeskQuickRequest: async (payload = {}) => {
    const response = await api.post("/attendance/engine/desk-request", payload);
    return response.data;
  },

  listDeskQuickRequests: async (params = {}) => {
    const response = await api.get("/attendance/engine/desk-request", { params });
    return response.data;
  },

  updateDeskQuickRequestStatus: async (id, status, extra = {}) => {
    const response = await api.put(`/attendance/engine/desk-request/${id}/status`, {
      status,
      ...extra,
    });
    return response.data;
  },

  requestDeskCancellation: async (id, comments = "") => {
    const response = await api.post(
      `/attendance/engine/desk-request/${id}/request-cancellation`,
      { comments }
    );
    return response.data;
  },

  selectRestrictedHoliday: async (payload = {}) => {
    const response = await api.post("/attendance/engine/restricted-holiday", payload);
    return response.data;
  },

  listRestrictedHolidays: async (params = {}) => {
    const response = await api.get("/attendance/engine/restricted-holiday", { params });
    return response.data;
  },

  updateRestrictedHolidayStatus: async (id, status) => {
    const response = await api.put(`/attendance/engine/restricted-holiday/${id}/status`, {
      status,
    });
    return response.data;
  },

  downloadPayrollAttendanceSummaryTemplate: async () => {
    const response = await api.get("/attendance/engine/payroll-summary/template", {
      responseType: "blob",
    });
    return response.data;
  },

  importPayrollAttendanceSummary: async (payMonth, file) => {
    const form = new FormData();
    form.append("payMonth", payMonth);
    form.append("file", file);
    const response = await api.post("/attendance/engine/payroll-summary/import", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getAttendanceHistory: async (
    month = null,
    year = null,
    page = 1,
    limit = 20,
    search = "",
    employeeIds = null
  ) => {
    const params = {};
    if (month !== null && year !== null) {
      params.month = month;
      params.year = year;
    }
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (search) params.search = search;
    if (employeeIds && employeeIds.length > 0) {
      params.employeeIds = employeeIds.join(","); // Send as comma-separated string
    }
    // ✅ REMOVED: timestamp parameter (_t) - enables browser caching for 5 minutes
    // Reduces API calls by 80% for the same filters/month

    const response = await api.get("/attendance/history", { params });
    return response.data;
  },

  // Optimized bulk history fetch for large employee lists.
  // Uses POST body to avoid very long query strings and supports lightweight mode.
  getAttendanceHistoryBulk: async ({
    month,
    year,
    page = 1,
    limit = 5000,
    search = "",
    employeeIds = [],
    lightweight = true,
    bulkMode = true,
  } = {}) => {
    const payload = {
      month,
      year,
      page,
      limit,
      search,
      employeeIds,
      lightweight,
      bulkMode,
    };
    // This endpoint can be heavy for large datasets; allow a longer timeout
    // than the global 60s to avoid client-side abort at exactly 60s.
    const response = await api.post("/attendance/history/query", payload, {
      timeout: 180000,
    });
    return response.data;
  },

  /**
   * GET /attendance/lop-reversal-summary?month=1&year=2025
   * Returns { data: { [employeeId]: lopReversalDays } } for the given payroll month.
   */
  getLOPReversalSummary: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/lop-reversal-summary", { params });
    return response.data;
  },

  /**
   * GET /attendance/lop-reversal-report?month=&year=
   * Detailed report rows for the selected payroll month.
   */
  getLOPReversalReport: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/lop-reversal-report", { params });
    return response.data;
  },

  /** Cut-off ledger impact for a pay month (display only — no value changes). */
  getCutoffImpact: async (payMonth) => {
    const response = await api.get("/attendance/cutoff-impact", {
      params: { payMonth: String(payMonth || "").slice(0, 7) },
    });
    return response.data;
  },

  getAttendanceChecksReport: async (params = {}) => {
    const response = await api.get("/attendance/reports/attendance-checks", { params });
    return response.data;
  },

  getDailyAttendanceReportV2: async (params = {}) => {
    const response = await api.get("/attendance/reports/daily-attendance", { params });
    return response.data;
  },

  getWhoAreInReport: async (params = {}) => {
    const response = await api.get("/attendance/reports/who-are-in", { params });
    return response.data;
  },

  getLeaveBalanceReport: async (params = {}) => {
    const response = await api.get("/attendance/reports/leave-balance", { params });
    return response.data;
  },

  /**
   * DELETE /attendance/lop-reversal-entry
   * Admin: remove one saved LOP reversal entry and restore previous status.
   */
  deleteLOPReversalEntry: async ({ employeeId, date }) => {
    const response = await api.delete("/attendance/lop-reversal-entry", {
      data: { employeeId, date },
    });
    return response.data;
  },

  /**
   * GET /attendance/my-history?month=1&year=2026
   * Returns attendance for the logged-in employee only (employee portal).
   * Use this instead of getAttendanceHistory in employee attendance views.
   */
  getMyAttendanceHistory: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/my-history", { params });
    return response.data;
  },

  /**
   * PATCH /attendance/my-lop-reversal
   * Employee self-service: change only LOP/LWP days in locked months.
   */
  updateMyLOPReversal: async (date, status, remarks) => {
    const payload = { date, status, ...(remarks ? { remarks } : {}) };
    const response = await api.patch("/attendance/my-lop-reversal", payload);
    return response.data;
  },

  // Optimized chunked API: returns employees + their attendance in a single request
  // Perfect for infinite scroll: 100 employees + 3000 attendance records (30 days × 100)
  // Instead of 50,000 records fetched upfront
  getChunkedAttendance: async (
    month = null,
    year = null,
    page = 1,
    limit = 100,
    search = "",
    filters = {}
  ) => {
    const params = {
      page,
      limit,
      month: month || new Date().getMonth() + 1,
      year: year || new Date().getFullYear(),
      // 🚀 Request only essential fields to reduce payload size
      fields:
        "id,emp_code,name,joining_date,relieved_date,employment_status,email",
    };
    if (search) params.search = search;
    
    // ✅ CRITICAL: Always include companyId to filter by current company
    // Try multiple sources: direct localStorage key, user object, or companyData
    let companyId = getAuthItem("companyId");
    
    // Fallback 1: Try to get from currentUser object
    if (!companyId || companyId === "null" || companyId === "undefined") {
      try {
        const currentUser = getAuthItem("currentUser");
        if (currentUser) {
          const user = JSON.parse(currentUser);
          companyId = user.companyId || user.company_id;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Fallback 2: Try to get from companyData.id (priority: id field)
    if (!companyId || companyId === "null" || companyId === "undefined") {
      try {
        const companyData = getAuthItem("companyData");
        if (companyData) {
          const company = JSON.parse(companyData);
          // Use company.id as primary source (as per user requirement)
          companyId = company.id || company.companyId;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Only add companyId if it's a valid, non-empty value
    if (companyId && companyId !== "null" && companyId !== "undefined" && String(companyId).trim() !== "") {
      params.companyId = String(companyId).trim();
    }
    
    // Merge any additional filters (but don't override companyId if already set)
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "undefined" && value !== "null") {
        // Don't override companyId if it was already set from localStorage
        if (key === "companyId" && params.companyId) {
          return; // Skip, keep the localStorage value
        }
        params[key] = value;
      }
    });
    // ✅ REMOVED: timestamp parameter (_t) - it prevented browser caching!
    // Now responses will be cached for 5 minutes, reducing redundant requests by 80%

    const response = await api.get("/attendance/chunked", { params });
    return response.data;
  },

  // 🚀 NEW: Load 5 chunks (500 employees) in parallel for MAXIMUM SPEED
  // Load chunks [1, 2, 3, 4, 5] simultaneously = 5x faster than sequential
  getMultipleChunkedAttendanceMax: async (
    month = null,
    year = null,
    pages = [1, 2, 3, 4, 5], // Load 5 chunks (500 employees) by default
    limit = 100,
    search = "",
    filters = {}
  ) => {
    try {
      // ✅ CRITICAL: Always include companyId to filter by current company
      // Try multiple sources: direct localStorage key, user object, or companyData
      let companyId = getAuthItem("companyId");
      
      // Fallback 1: Try to get from currentUser object
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const currentUser = getAuthItem("currentUser");
          if (currentUser) {
            const user = JSON.parse(currentUser);
            companyId = user.companyId || user.company_id;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Fallback 2: Try to get from companyData.id (priority: id field)
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const companyData = getAuthItem("companyData");
          if (companyData) {
            const company = JSON.parse(companyData);
            // Use company.id as primary source (as per user requirement)
            companyId = company.id || company.companyId;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Only use companyId if it's a valid, non-empty value
      const validCompanyId = companyId && 
        companyId !== "null" && 
        companyId !== "undefined" && 
        String(companyId).trim() !== "" 
        ? String(companyId).trim() 
        : null;
      
      // 🚀 Create 5 parallel requests (much faster than sequential!)
      const requests = pages.map((page) => {
        const requestParams = {
          page,
          limit,
          month: month || new Date().getMonth() + 1,
          year: year || new Date().getFullYear(),
          fields:
            "id,emp_code,name,joining_date,relieved_date,employment_status,email",
          search: search || undefined,
          ...filters,
        };
        
        // Only add companyId if it's valid
        if (validCompanyId) {
          requestParams.companyId = validCompanyId;
        }
        
        return api.get("/attendance/chunked", { params: requestParams });
      });

      // Wait for all 5 requests in parallel (500 employees instant!)
      const results = await Promise.all(requests);
      const data = results.map((r) => r.data);

      // Merge all results into one
      return {
        success: data.every((d) => d.success),
        employees: data.flatMap((d) => d.employees || []),
        attendance: Object.assign({}, ...data.map((d) => d.attendance || {})),
        pagination: data[data.length - 1]?.pagination || {},
      };
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  // 🚀 NEW: Load multiple chunks in parallel for 3x faster data loading
  // Instead of loading chunks sequentially (100, then 200, then 300...)
  // Load chunks 2, 3, 4 in parallel while displaying chunk 1
  getMultipleChunkedAttendance: async (
    month = null,
    year = null,
    pages = [1, 2, 3], // Which page numbers to load
    limit = 100,
    search = "",
    filters = {}
  ) => {
    try {
      // ✅ CRITICAL: Always include companyId to filter by current company
      // Try multiple sources: direct localStorage key, user object, or companyData
      let companyId = getAuthItem("companyId");
      
      // Fallback 1: Try to get from currentUser object
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const currentUser = getAuthItem("currentUser");
          if (currentUser) {
            const user = JSON.parse(currentUser);
            companyId = user.companyId || user.company_id;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Fallback 2: Try to get from companyData.id (priority: id field)
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const companyData = getAuthItem("companyData");
          if (companyData) {
            const company = JSON.parse(companyData);
            // Use company.id as primary source (as per user requirement)
            companyId = company.id || company.companyId;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Only use companyId if it's a valid, non-empty value
      const validCompanyId = companyId && 
        companyId !== "null" && 
        companyId !== "undefined" && 
        String(companyId).trim() !== "" 
        ? String(companyId).trim() 
        : null;
      
      // Create parallel requests for all pages (much faster than sequential!)
      const requests = pages.map((page) => {
        const requestParams = {
          page,
          limit,
          month: month || new Date().getMonth() + 1,
          year: year || new Date().getFullYear(),
          fields:
            "id,emp_code,name,joining_date,relieved_date,employment_status,email",
          search: search || undefined,
          ...filters,
        };
        
        // Only add companyId if it's valid
        if (validCompanyId) {
          requestParams.companyId = validCompanyId;
        }
        
        return api.get("/attendance/chunked", { params: requestParams });
      });

      // Wait for all requests in parallel (3 requests simultaneously = 3x faster)
      const results = await Promise.all(requests);
      const data = results.map((r) => r.data);

      // Merge all results into one
      return {
        success: data.every((d) => d.success),
        employees: data.flatMap((d) => d.employees || []),
        attendance: Object.assign({}, ...data.map((d) => d.attendance || {})),
        pagination: data[data.length - 1]?.pagination || {},
      };
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  // Backwards-compatible alias used by some components
  getMonthly: async (year = null, month = null) => {
    try {
      const params = {};
      if (month !== null && year !== null) {
        params.month = month;
        params.year = year;
      }
      // ✅ REMOVED: timestamp parameter - allows browser caching
      // Responses now cached for 5 minutes, reducing API load by 80%
      const response = await api.get("/attendance/history", { params });
      return response.data;
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  getAttendanceStats: async () => {
    const response = await api.get("/attendance/stats");
    return response.data;
  },

  bulkMarkAttendance: async (attendanceData, selectedDate = null) => {
    // If caller already provided a pre-built payload matching backend format,
    // accept it directly. This allows the frontend to build records with
    // employeeId, date, status and remarks and send exactly as backend expects.
    if (
      attendanceData &&
      typeof attendanceData === "object" &&
      Array.isArray(attendanceData.employeeAttendance)
    ) {
      const response = await api.post("/attendance/bulk-mark", attendanceData);
      return response.data;
    }

    // selectedDate is "2025-11-01"
    const dateToUse = selectedDate || new Date().toISOString().slice(0, 10);

    // Build year-month prefix from date string to avoid timezone issues
    const yearMonthPrefix = dateToUse.substring(0, 8); // "2025-11-"

    const employeeAttendance = Object.entries(attendanceData).flatMap(
      ([empId, days]) =>
        Object.entries(days).map(([day, status]) => ({
          employeeId: parseInt(empId, 10),
          date: `${yearMonthPrefix}${String(day).padStart(2, "0")}`,
          status,
          remarks: "",
        }))
    );

    const transformed = {
      date: dateToUse,
      employeeAttendance,
    };

    const response = await api.post("/attendance/bulk-mark", transformed);
    return response.data;
  },

  lockAttendance: async (month, year, payload = {}) => {
    const response = await api.put(`/attendance/lock/${month}/${year}`, payload);
    return response.data;
  },

  getAttendanceProcessStatus: async (month, year) => {
    const response = await api.get(`/attendance/process-status/${month}/${year}`);
    return response.data;
  },

  updateAttendanceProcessEmployees: async (payload = {}) => {
    const response = await api.post("/attendance/process-employees", payload);
    return response.data;
  },

  requestUnlockAttendance: async (month, year) => {
    const response = await api.put(`/attendance/unlock-request/${month}/${year}`);
    return response.data;
  },

  requestLockAttendance: async (month, year) => {
    const response = await api.put(`/attendance/lock-request/${month}/${year}`);
    return response.data;
  },

  unlockAttendance: async (month, year, payload = {}) => {
    const response = await api.put(`/attendance/unlock/${month}/${year}`, payload);
    return response.data;
  },

  getAttendancePayrollProgress: async (month, year) => {
    const response = await api.get(`/attendance/payroll-progress/${month}/${year}`);
    return response.data;
  },

  importAttendance: async (formData) => {
    const response = await api.post("/attendance/import", formData, {
      // Let browser set multipart boundary automatically.
      // Manually forcing Content-Type can break some uploads.
      timeout: 120000,
    });
    return response.data;
  },

  exportAttendance: async (month, year) => {
    const response = await api.get(`/attendance/export/${month}/${year}`, {
      responseType: "blob",
    });
    return response;
  },

  downloadTemplate: async (month, year, employeeIds = null) => {
    const params = { _t: Date.now() };
    if (month !== undefined && month !== null) {
      // Convert 0-indexed month to 1-indexed (0-11 => 1-12)
      params.month = typeof month === "number" ? month + 1 : month;
    }
    if (year !== undefined && year !== null) {
      params.year = year;
    }
    if (Array.isArray(employeeIds) && employeeIds.length > 0) {
      params.employeeIds = employeeIds.join(",");
    }
    const response = await api.get("/attendance/template", {
      params,
      responseType: "blob",
      // Template builds all employees + Excel workbook; allow longer than default 60s.
      timeout: 180000,
      // Do NOT send Cache-Control / Pragma here — they are non-simple headers and
      // trigger a CORS preflight that fails on hosted cross-origin (tenant UI →
      // newadmin API). Localhost works because /api is same-origin via the CRA proxy.
      // Cache busting is already handled by params._t above.
    });
    return response;
  },
  // Attendance configuration codes (custom company mappings)
  getConfigurations: async () => {
    const response = await api.get("/attendance/configurations");
    const d = response.data;
    // Return full object when present so halfDayDivider/halfDayPrefix are preserved; else inner data or []
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },

  saveConfigurations: async (configs) => {
    // Accept either an array of configs or an object with { codes: [...], halfDayDivider?, halfDayPrefix? }
    // Backend expects: { codes: [ { meaning, customCode, importCode, exportCode } ], halfDayDivider?, halfDayPrefix? } (company-wise)
    let body;
    if (Array.isArray(configs)) {
      body = {
        codes: configs.map((c) => ({
          meaning: c.meaning,
          customCode: c.customCode,
          importCode: c.importCode,
          exportCode: c.exportCode,
        })),
      };
    } else if (configs && typeof configs === "object") {
      body = configs;
    } else {
      body = { codes: [] };
    }

    const response = await api.post("/attendance/configurations", body);
    const d = response.data;
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },

  resetConfigurations: async () => {
    const response = await api.post("/attendance/configurations/reset");
    const d = response.data;
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },

  listActionRecords: async (actionKey, params = {}) => {
    const response = await api.get(
      `/attendance/actions/${encodeURIComponent(actionKey)}/records`,
      { params }
    );
    return response.data;
  },
};

// Payroll services
const resolveCompanyIdFromStorage = () => {
  let companyId = getAuthItem("companyId");

  if (!companyId || companyId === "null" || companyId === "undefined") {
    try {
      const user = getAuthJson("currentUser", null);
      if (user) {
        companyId = user.companyId || user.company_id;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (!companyId || companyId === "null" || companyId === "undefined") {
    try {
      const company = getAuthJson("companyData", null);
      if (company) {
        companyId = company.id || company.companyId;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (
    companyId &&
    companyId !== "null" &&
    companyId !== "undefined" &&
    String(companyId).trim() !== ""
  ) {
    return String(companyId).trim();
  }

  return null;
};

export const payrollService = {
  generatePayslip: async (payrollData) => {
    const response = await api.post("/payroll/generate", payrollData);
    return response.data;
  },

  getPayslips: async (employeeId) => {
    const response = await api.get(`/payroll/payslips/${employeeId}`);
    return response.data;
  },

  submitTaxDeclaration: async (declarationData) => {
    const response = await api.post(
      "/payroll/tax-declaration",
      declarationData
    );
    return response.data;
  },

  getTaxDeclaration: async (employeeId) => {
    const response = await api.get(`/payroll/tax-declaration/${employeeId}`);
    return response.data;
  },

  updateTaxDeclarationStatus: async (id, status) => {
    const response = await api.put(`/payroll/tax-declaration/${id}`, {
      status,
    });
    return response.data;
  },

  bulkImportDeclarations: async (formData) => {
    const response = await api.post(
      "/payroll/declarations/bulk-import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  exportDeclarations: async (financialYear, taxRegime = null) => {
    const params = { financialYear };
    if (taxRegime) {
      params.taxRegime = taxRegime;
    }
    const response = await api.get("/payroll/declarations/export", {
      params,
      responseType: "blob",
    });
    return response;
  },

  bulkApproveDeclarations: async (declarationIds) => {
    const response = await api.put("/payroll/declarations/bulk-approve", {
      declarationIds,
    });
    return response.data;
  },

  downloadDeclarationTemplate: async (payMonth) => {
    const response = await api.get("/payroll/declarations/template", {
      params: payMonth ? { payMonth } : {},
      responseType: "blob",
    });
    return response;
  },

  // Investment declaration headers (tax regime + status) for all employees in a year
  getInvestmentHeaders: async (financialYear) => {
    const response = await api.get("/payroll/investment-headers", {
      params: { financialYear },
    });
    return response.data;
  },

  // Normalized investment declaration (header + sections + attachments)
  getInvestmentData: async (employeeId, financialYear) => {
    const params = financialYear
      ? `?financialYear=${encodeURIComponent(financialYear)}`
      : "";
    const response = await api.get(
      `/payroll/investment-data/${employeeId}${params}`
    );
    return response.data;
  },

  saveInvestmentData: async (employeeId, investmentData) => {
    const response = await api.post(
      `/payroll/investment-data/${employeeId}`,
      investmentData
    );
    return response.data;
  },

  deleteInvestmentData: async (employeeId, financialYear) => {
    const response = await api.delete(
      `/payroll/investment-data/${employeeId}`,
      {
        params: { financialYear },
      }
    );
    return response.data;
  },

  // Proof upload and attachments
  uploadProof: async (file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);

    // R2 naming hints so backend can use deterministic per-employee, per-slot keys
    const { employeeId, slotKey, storageFileName } = options || {};
    if (employeeId != null) {
      formData.append("employeeId", employeeId);
    }
    if (slotKey) {
      formData.append("slotKey", slotKey);
    }
    if (storageFileName) {
      formData.append("storageFileName", storageFileName);
    }

    const response = await api.post("/payroll/upload-proof", formData);
    return response.data; // expects { success, data: { fileUrl, fileName, fileSize, ... } }
  },

  createInvestmentAttachment: async (declarationId, payload) => {
    const response = await api.post(
      `/payroll/investment-attachments/${declarationId}`,
      payload
    );
    return response.data;
  },

  deleteInvestmentAttachment: async (attachmentId) => {
    const response = await api.delete(
      `/payroll/investment-attachments/${attachmentId}`
    );
    return response.data;
  },

  reviewInvestmentAttachment: async (attachmentId, { status, comment }) => {
    const response = await api.patch(
      `/payroll/investment-attachments/${attachmentId}`,
      { status, comment }
    );
    return response.data;
  },

  getInvestmentAttachmentUrl: async (attachmentId) => {
    const response = await api.get(
      `/payroll/investment-attachments/${attachmentId}/url`
    );
    return response.data;
  },

  getInvestmentAttachmentDownloadUrl: async (attachmentId) => {
    const response = await api.get(
      `/payroll/investment-attachments/${attachmentId}/download-url`
    );
    return response.data;
  },

  // Payroll settings
  getPayrollSettings: async () => {
    const companyId = resolveCompanyIdFromStorage();
    const params = companyId ? { companyId } : {};
    const response = await api.get("/payroll/settings", { params });
    return response.data;
  },

  upsertPayrollSettings: async (settingsData) => {
    const companyId = resolveCompanyIdFromStorage();
    const payload = companyId
      ? { ...settingsData, companyId }
      : settingsData;
    const response = await api.put("/payroll/settings", payload);
    return response.data;
  },
};

// Investment services
export const investmentService = {
  getInvestments: async (employeeId) => {
    // Follows the same standard as other services
    const response = await api.get(`/investments/${employeeId}`);
    return response.data;
  },
};

// Department services
export const departmentService = {
  createDepartment: async (departmentData) => {
    const response = await api.post("/departments", departmentData);
    return response.data;
  },

  getDepartments: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/departments", { params });
    return response.data;
  },

  getDepartment: async (id) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  updateDepartment: async (id, departmentData) => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },

  deleteDepartment: async (id) => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },
};

export const subDepartmentService = {
  createSubDepartment: async (subDepartmentData) => {
    const response = await api.post("/sub-departments", subDepartmentData);
    return response.data;
  },

  getAllSubDepartments: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/sub-departments", { params });
    return response.data;
  },

  getSubDepartment: async (id) => {
    const response = await api.get(`/sub-departments/${id}`);
    return response.data;
  },

  updateSubDepartment: async (id, subDepartmentData) => {
    const response = await api.put(`/sub-departments/${id}`, subDepartmentData);
    return response.data;
  },

  deleteSubDepartment: async (id) => {
    const response = await api.delete(`/sub-departments/${id}`);
    return response.data;
  },
};

// Designation services
export const designationService = {
  createDesignation: async (designationData) => {
    const response = await api.post("/designations", designationData);
    return response.data;
  },

  getDesignations: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/designations", { params });
    return response.data;
  },

  getDesignation: async (id) => {
    const response = await api.get(`/designations/${id}`);
    return response.data;
  },

  updateDesignation: async (id, designationData) => {
    const response = await api.put(`/designations/${id}`, designationData);
    return response.data;
  },

  deleteDesignation: async (id) => {
    const response = await api.delete(`/designations/${id}`);
    return response.data;
  },
};

// Branch services
export const branchService = {
  createBranch: async (branchData) => {
    const response = await api.post("/branches", branchData);
    return response.data;
  },

  getBranches: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/branches", { params });
    return response.data;
  },

  getBranch: async (id) => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },

  updateBranch: async (id, branchData) => {
    const response = await api.put(`/branches/${id}`, branchData);
    return response.data;
  },

  deleteBranch: async (id) => {
    const response = await api.delete(`/branches/${id}`);
    return response.data;
  },
};

// Grades services
export const gradesService = {
  createGrade: async (gradeData) => {
    const response = await api.post("/grades", gradeData);
    return response.data;
  },

  getGrades: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/grades", { params });
    return response.data;
  },

  updateGrade: async (id, gradeData) => {
    const response = await api.put(`/grades/${id}`, gradeData);
    return response.data;
  },

  deleteGrade: async (id) => {
    const response = await api.delete(`/grades/${id}`);
    return response.data;
  },
};

export const categoryService = {
  createCategory: async (categoryData) => {
    const response = await api.post("/categories", categoryData);
    return response.data;
  },

  getAllCategories: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/categories", { params });
    return response.data;
  },

  updateCategory: async (id, categoryData) => {
    const response = await api.put(`/categories/${id}`, categoryData);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },

  getMinimumWages: async (categoryId) => {
    const response = await api.get(`/categories/${categoryId}/minimum-wages`);
    return response.data;
  },

  addMinimumWage: async (categoryId, payload) => {
    const response = await api.post(
      `/categories/${categoryId}/minimum-wages`,
      payload
    );
    return response.data;
  },

  deleteMinimumWage: async (categoryId, mwId) => {
    const response = await api.delete(
      `/categories/${categoryId}/minimum-wages/${mwId}`
    );
    return response.data;
  },

  resolveMinimumWage: async (categoryId, month, branchId = null) => {
    const response = await api.get(`/categories/minimum-wages/resolve`, {
      params: {
        categoryId,
        month,
        ...(branchId != null && branchId !== "" ? { branchId } : {}),
      },
    });
    return response.data;
  },

  bulkImportMinimumWages: async (rows) => {
    const response = await api.post(`/categories/minimum-wages/bulk`, { rows });
    return response.data;
  },
};

export const reasonsService = {
  createReason: async (reasonData) => {
    const response = await api.post("/reasons", reasonData);
    return response.data;
  },

  getAllTerminationReasons: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/reasons/termination", { params });
    return response.data;
  },

  getAllResignationReasons: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/reasons/resignation", { params });
    return response.data;
  },

  updateReason: async (id, reasonData) => {
    const response = await api.put(`/reasons/${id}`, reasonData);
    return response.data;
  },

  deleteReason: async (id) => {
    const response = await api.delete(`/reasons/${id}`);
    return response.data;
  },
};

// Employee Code Structure services
export const employeeCodeStructureService = {
  getCurrentStructure: async () => {
    const response = await api.get("/employee-code-structure/current");
    return response.data;
  },

  getStructureHistory: async () => {
    const response = await api.get("/employee-code-structure/history");
    return response.data;
  },

  saveStructure: async (structureData) => {
    const response = await api.post(
      "/employee-code-structure/save",
      structureData
    );
    return response.data;
  },

  generatePreview: async (previewData) => {
    const response = await api.post(
      "/employee-code-structure/preview",
      previewData
    );
    return response.data;
  },

  getNextEmployeeCode: async () => {
    const response = await api.get("/employee-code-structure/next");
    return response.data;
  },

  updateCurrentCount: async (countData) => {
    const response = await api.put(
      "/employee-code-structure/update-count",
      countData
    );
    return response.data;
  },
};

// Confirmation services (legacy)
export const confirmationService = {
  createConfirmation: async (confirmationData) => {
    const response = await api.post("/confirmations", confirmationData);
    return response.data;
  },

  getConfirmationHistory: async (employeeId) => {
    const response = await api.get(`/confirmations/history/${employeeId}`);
    return response.data;
  },

  getConfirmation: async (id) => {
    const response = await api.get(`/confirmations/${id}`);
    return response.data;
  },
};

// Confirmation runtime (policy-driven)
export const employeeConfirmationService = {
  getContext: async (employeeId) => {
    const response = await api.get(`/employee-confirmations/employee/${employeeId}/context`);
    return response.data;
  },

  getByEmployee: async (employeeId) => {
    const response = await api.get(`/employee-confirmations/employee/${employeeId}`);
    return response.data;
  },

  reconcile: async (employeeId) => {
    const response = await api.post(`/employee-confirmations/employee/${employeeId}/reconcile`);
    return response.data;
  },

  listInsights: async () => {
    const response = await api.get("/employee-confirmations/insights");
    return response.data;
  },

  listApprovalsDesk: async () => {
    const response = await api.get("/employee-confirmations/approvals-desk");
    return response.data;
  },

  listMyRequestsDesk: async () => {
    const response = await api.get("/employee-confirmations/my-requests-desk");
    return response.data;
  },

  getRequest: async (id) => {
    const response = await api.get(`/employee-confirmations/${id}`);
    return response.data;
  },

  initiateWorkflow: async (id) => {
    const response = await api.post(`/employee-confirmations/${id}/initiate-workflow`);
    return response.data;
  },

  approve: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/approve`, body);
    return response.data;
  },

  reject: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/reject`, body);
    return response.data;
  },

  sendBack: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/send-back`, body);
    return response.data;
  },

  hold: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/hold`, body);
    return response.data;
  },

  extendProbation: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/extend`, body);
    return response.data;
  },

  confirm: async (id, body) => {
    const response = await api.post(`/employee-confirmations/${id}/confirm`, body);
    return response.data;
  },

  completeChecklistItem: async (id, itemId, body) => {
    const response = await api.post(
      `/employee-confirmations/${id}/checklist/${itemId}/complete`,
      body
    );
    return response.data;
  },
};

/** Policy-driven transfer runtime (`/employee-transfers`). */
export const employeeTransferService = {
  listInsights: async () => {
    const response = await api.get("/employee-transfers/insights");
    return response.data;
  },
  listApprovalsDesk: async () => {
    const response = await api.get("/employee-transfers/approvals-desk");
    return response.data;
  },
  listMyRequestsDesk: async () => {
    const response = await api.get("/employee-transfers/my-requests-desk");
    return response.data;
  },
  listRequests: async (params = {}) => {
    const response = await api.get("/employee-transfers", { params });
    return response.data;
  },
  getRequest: async (id) => {
    const response = await api.get(`/employee-transfers/${id}`);
    return response.data;
  },
  getByEmployee: async (employeeId) => {
    const response = await api.get(`/employee-transfers/employee/${employeeId}`);
    return response.data;
  },
  resolvePolicy: async (employeeId) => {
    const response = await api.get(`/employee-transfers/employee/${employeeId}/resolve-policy`);
    return response.data;
  },
  createRequest: async (payload) => {
    const response = await api.post("/employee-transfers", payload);
    return response.data;
  },
  submitRequest: async (id) => {
    const response = await api.post(`/employee-transfers/${id}/submit`);
    return response.data;
  },
  initiateWorkflow: async (id) => {
    const response = await api.post(`/employee-transfers/${id}/initiate-workflow`);
    return response.data;
  },
  approve: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/approve`, body);
    return response.data;
  },
  reject: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/reject`, body);
    return response.data;
  },
  sendBack: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/send-back`, body);
    return response.data;
  },
  hold: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/hold`, body);
    return response.data;
  },
  cancel: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/cancel`, body);
    return response.data;
  },
  completeChecklistItem: async (id, itemId, body) => {
    const response = await api.post(
      `/employee-transfers/${id}/checklist/${itemId}/complete`,
      body
    );
    return response.data;
  },
  acknowledge: async (id, body) => {
    const response = await api.post(`/employee-transfers/${id}/acknowledge`, body);
    return response.data;
  },
  getTimeline: async (id) => {
    const response = await api.get(`/employee-transfers/${id}/timeline`);
    return response.data;
  },
};

/** Policy-driven separation / resignation runtime (`/employee-separations`). */
export const employeeSeparationService = {
  listInsights: async () => {
    const response = await api.get("/employee-separations/insights");
    return response.data;
  },
  listApprovalsDesk: async () => {
    const response = await api.get("/employee-separations/approvals-desk");
    return response.data;
  },
  listMyRequestsDesk: async () => {
    const response = await api.get("/employee-separations/my-requests-desk");
    return response.data;
  },
  listRequests: async (params = {}) => {
    const response = await api.get("/employee-separations", { params });
    return response.data;
  },
  getRequest: async (id) => {
    const response = await api.get(`/employee-separations/${id}`);
    return response.data;
  },
  getByEmployee: async (employeeId) => {
    const response = await api.get(`/employee-separations/employee/${employeeId}`);
    return response.data;
  },
  resolvePolicy: async (employeeId, params = {}) => {
    const response = await api.get(`/employee-separations/employee/${employeeId}/resolve-policy`, {
      params,
    });
    return response.data;
  },
  createRequest: async (payload) => {
    const response = await api.post("/employee-separations", payload);
    return response.data;
  },
  submitRequest: async (id) => {
    const response = await api.post(`/employee-separations/${id}/submit`);
    return response.data;
  },
  initiateWorkflow: async (id) => {
    const response = await api.post(`/employee-separations/${id}/initiate-workflow`);
    return response.data;
  },
  approve: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/approve`, body);
    return response.data;
  },
  reject: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/reject`, body);
    return response.data;
  },
  sendBack: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/send-back`, body);
    return response.data;
  },
  hold: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/hold`, body);
    return response.data;
  },
  cancel: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/cancel`, body);
    return response.data;
  },
  completeChecklistItem: async (id, itemId, body) => {
    const response = await api.post(
      `/employee-separations/${id}/checklist/${itemId}/complete`,
      body
    );
    return response.data;
  },
  completeClearance: async (id, nocId, body) => {
    const response = await api.post(
      `/employee-separations/${id}/clearance/${nocId}/complete`,
      body
    );
    return response.data;
  },
  initiateFnf: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/fnf/initiate`, body);
    return response.data;
  },
  completeFnf: async (id, settlementId, body) => {
    const response = await api.post(
      `/employee-separations/${id}/fnf/${settlementId}/complete`,
      body
    );
    return response.data;
  },
  completeExitInterview: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/exit-interview/complete`, body);
    return response.data;
  },
  acknowledge: async (id, body) => {
    const response = await api.post(`/employee-separations/${id}/acknowledge`, body);
    return response.data;
  },
  getTimeline: async (id) => {
    const response = await api.get(`/employee-separations/${id}/timeline`);
    return response.data;
  },
};

// Legacy Transfer services (history / older records)
export const transferService = {
  createTransfer: async (transferData) => {
    const response = await api.post("/transfers", transferData);
    return response.data;
  },

  getTransferHistory: async (employeeId) => {
    const response = await api.get(`/transfers/history/${employeeId}`);
    return response.data;
  },

  getTransfer: async (id) => {
    const response = await api.get(`/transfers/${id}`);
    return response.data;
  },
};

// Salary services
export const salaryStructureService = {
  // Components
  listComponents: async () => {
    const response = await api.get("/salary-structures/components");
    return response.data;
  },
  createComponent: async (data) => {
    const response = await api.post("/salary-structures/components", data);
    return response.data;
  },
  updateComponent: async (id, data) => {
    const response = await api.put(`/salary-structures/components/${id}`, data);
    return response.data;
  },
  deleteComponent: async (id) => {
    const response = await api.delete(`/salary-structures/components/${id}`);
    return response.data;
  },
  // TDS Reference Heads
  listTdsReferenceHeads: async (params = {}) => {
    const response = await api.get("/salary-structures/tds-reference-heads", { params });
    return response.data;
  },
  getTdsReferenceHead: async (id) => {
    const response = await api.get(`/salary-structures/tds-reference-heads/${id}`);
    return response.data;
  },
  createTdsReferenceHead: async (data) => {
    const response = await api.post("/salary-structures/tds-reference-heads", data);
    return response.data;
  },
  updateTdsReferenceHead: async (id, data) => {
    const response = await api.put(`/salary-structures/tds-reference-heads/${id}`, data);
    return response.data;
  },
  suggestTdsReferenceHead: async (data) => {
    const response = await api.post("/salary-structures/tds-reference-heads/suggest", data);
    return response.data;
  },
  // Templates
  listTemplates: async () => {
    const response = await api.get("/salary-structures/templates");
    return response.data;
  },
  getTemplate: async (id) => {
    const response = await api.get(`/salary-structures/templates/${id}`);
    return response.data;
  },
  createTemplate: async (data) => {
    const response = await api.post("/salary-structures/templates", data);
    return response.data;
  },
  updateTemplate: async (id, data) => {
    const response = await api.put(`/salary-structures/templates/${id}`, data);
    return response.data;
  },
  syncTemplateReimbursements: async (templateId, body = {}) => {
    const response = await api.post(
      `/salary-structures/templates/${templateId}/sync-reimbursements`,
      body
    );
    return response.data;
  },
  getReimbSyncJob: async (jobId) => {
    const response = await api.get(`/salary-structures/reimb-sync-jobs/${jobId}`);
    return response.data;
  },
  /** Immediate DB save of one structure component (+ history version). */
  patchTemplateComponent: async (templateId, componentId, component, note) => {
    const response = await api.patch(
      `/salary-structures/templates/${templateId}/components/${componentId}`,
      { component, note }
    );
    return response.data;
  },
  restoreTemplateComponentHistory: async (templateId, componentId, historyId) => {
    const response = await api.post(
      `/salary-structures/templates/${templateId}/components/${componentId}/history/${historyId}/restore`
    );
    return response.data;
  },
  deleteTemplateComponentHistory: async (templateId, componentId, historyId) => {
    const response = await api.delete(
      `/salary-structures/templates/${templateId}/components/${componentId}/history/${historyId}`
    );
    return response.data;
  },
  getPerformanceBonusAccrual: async (employeeId, { payMonth, payoutPercent } = {}) => {
    const response = await api.get(
      `/salary-structures/employee/${employeeId}/performance-bonus-accrual`,
      { params: { payMonth, payoutPercent } }
    );
    return response.data;
  },
  deleteTemplate: async (id) => {
    const response = await api.delete(`/salary-structures/templates/${id}`);
    return response.data;
  },
  // Statutory
  getStatutoryConfig: async () => {
    const response = await api.get("/salary-structures/statutory");
    return response.data;
  },
  updateStatutoryConfig: async (id, data) => {
    const response = await api.put(`/salary-structures/statutory/${id}`, data);
    return response.data;
  },
  // Compute
  computeStructure: async (data) => {
    const response = await api.post("/salary-structures/compute", data);
    return response.data;
  },
  // Employee structures
  listEmployeeStructures: async (employeeId) => {
    const response = await api.get(`/salary-structures/employee/${employeeId}`);
    return response.data;
  },
  getStructureTimeline: async (employeeId) => {
    const response = await api.get(`/salary-structures/employee/${employeeId}/timeline`);
    return response.data;
  },
  getStructureDeleteImpact: async (employeeId, structureId) => {
    const response = await api.get(
      `/salary-structures/employee/${employeeId}/versions/${structureId}/delete-impact`
    );
    return response.data;
  },
  deleteStructureVersion: async (employeeId, structureId) => {
    const response = await api.delete(
      `/salary-structures/employee/${employeeId}/versions/${structureId}`
    );
    return response.data;
  },
  getActiveStructure: async (employeeId) => {
    const response = await api.get(`/salary-structures/employee/${employeeId}/active`);
    return response.data;
  },
  saveEmployeeStructure: async (data) => {
    const response = await api.post("/salary-structures/employee", data);
    return response.data;
  },
  listActiveEmployeeStructureMap: async (templateId = null) => {
    const params = templateId ? { templateId } : {};
    const response = await api.get("/salary-structures/employee-active-map", { params });
    return response.data;
  },
  bulkManualImportEmployeeStructures: async (rows) => {
    const response = await api.post(
      "/salary-structures/employee/bulk-manual-import",
      { rows },
      { timeout: 180000 }
    );
    return response.data;
  },
};

/** Flexi Benefits / FBP claims, declarations, TDS rule versions */
export const flexiBenefitsService = {
  getAccess: async (params = {}) => {
    const response = await api.get("/flexi-benefits/access", { params });
    return response.data;
  },
  getHeadTemplate: async (code, params = {}) => {
    const response = await api.get(`/flexi-benefits/head-templates/${encodeURIComponent(code)}`, {
      params,
    });
    return response.data;
  },
  getBalances: async (params = {}) => {
    const response = await api.get("/flexi-benefits/balances", { params });
    return response.data;
  },
  getEligibleComponents: async (params = {}) => {
    const response = await api.get("/flexi-benefits/eligible-components", { params });
    return response.data;
  },
  saveComponentFlexiConfig: async (id, data) => {
    const response = await api.put(`/flexi-benefits/components/${id}/config`, data);
    return response.data;
  },
  listDeclarations: async (params = {}) => {
    const response = await api.get("/flexi-benefits/declarations", { params });
    return response.data;
  },
  upsertDeclaration: async (data) => {
    const response = await api.post("/flexi-benefits/declarations", data);
    return response.data;
  },
  revokeDeclaration: async (data) => {
    const response = await api.post("/flexi-benefits/declarations/revoke", data);
    return response.data;
  },
  listClaims: async (params = {}) => {
    const response = await api.get("/flexi-benefits/claims", { params });
    return response.data;
  },
  listLedger: async (params = {}) => {
    const response = await api.get("/flexi-benefits/ledger", { params });
    return response.data;
  },
  getYearEndSummary: async (params = {}) => {
    const response = await api.get("/flexi-benefits/year-end-summary", { params });
    return response.data;
  },
  submitClaim: async (data) => {
    const response = await api.post("/flexi-benefits/claims", data);
    return response.data;
  },
  decideClaim: async (id, data) => {
    const response = await api.post(`/flexi-benefits/claims/${id}/decide`, data);
    return response.data;
  },
  listHeadRuleVersions: async (headId) => {
    const response = await api.get(`/flexi-benefits/tds-heads/${headId}/rule-versions`);
    return response.data;
  },
  publishHeadRuleVersion: async (headId, data) => {
    const response = await api.post(`/flexi-benefits/tds-heads/${headId}/rule-versions`, data);
    return response.data;
  },
  previewHeadRule: async (headId, data) => {
    const response = await api.post(`/flexi-benefits/tds-heads/${headId}/preview-rule`, data);
    return response.data;
  },
  listPayrollPostings: async (params = {}) => {
    const response = await api.get("/flexi-benefits/payroll-postings", { params });
    return response.data;
  },
  markPaid: async (data) => {
    const response = await api.post("/flexi-benefits/claims/mark-paid", data);
    return response.data;
  },
  respondClaimQuery: async (id, data) => {
    const response = await api.post(`/flexi-benefits/claims/${id}/respond-query`, data);
    return response.data;
  },
  payProvisional: async (id, data) => {
    const response = await api.post(`/flexi-benefits/claims/${id}/provisional-pay`, data);
    return response.data;
  },
  supplementBill: async (id, data) => {
    const response = await api.post(`/flexi-benefits/claims/${id}/supplement-bill`, data);
    return response.data;
  },
  getFnfPending: async (params = {}) => {
    const response = await api.get("/flexi-benefits/fnf-pending", { params });
    return response.data;
  },
  settleFyEnd: async (data) => {
    const response = await api.post("/flexi-benefits/fy-end-settle", data);
    return response.data;
  },
};

/** Statutory compliance reports (Karnataka + Central PF/ESI) */
export const statutoryReportsService = {
  getCatalog: async (params = {}) => {
    const response = await api.get("/statutory-reports/catalog", { params });
    return response.data;
  },
  getPeriodOptions: async () => {
    const response = await api.get("/statutory-reports/period-options");
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get("/statutory-reports/profile");
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put("/statutory-reports/profile", data);
    return response.data;
  },
  listAliases: async () => {
    const response = await api.get("/statutory-reports/aliases");
    return response.data;
  },
  upsertAlias: async (data) => {
    const response = await api.post("/statutory-reports/aliases", data);
    return response.data;
  },
  deleteAlias: async (id) => {
    const response = await api.delete(`/statutory-reports/aliases/${id}`);
    return response.data;
  },
  generateReport: async (params = {}) => {
    const response = await api.get("/statutory-reports/generate", {
      params,
      timeout: 180000,
    });
    return response.data;
  },
  exportExcel: async (params = {}) => {
    try {
      const response = await api.get("/statutory-reports/export-excel", {
        params,
        responseType: "blob",
        timeout: 300000,
      });
      return response;
    } catch (error) {
      const blob = error?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          error.response.data = json;
        } catch {
          /* keep blob */
        }
      }
      throw error;
    }
  },
  exportPdf: async (params = {}) => {
    try {
      const response = await api.get("/statutory-reports/export-pdf", {
        params,
        responseType: "blob",
        timeout: 300000,
      });
      return response;
    } catch (error) {
      const blob = error?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          error.response.data = json;
        } catch {
          /* keep blob */
        }
      }
      throw error;
    }
  },
  listMaternity: async (params = {}) => {
    const response = await api.get("/statutory-reports/maternity", { params });
    return response.data;
  },
  upsertMaternity: async (data) => {
    const response = await api.post("/statutory-reports/maternity", data);
    return response.data;
  },
  listAccidents: async (params = {}) => {
    const response = await api.get("/statutory-reports/accidents", { params });
    return response.data;
  },
  createAccident: async (data) => {
    const response = await api.post("/statutory-reports/accidents", data);
    return response.data;
  },
};

export const salaryService = {
  createSalary: async (salaryData) => {
    const response = await api.post("/salaries", salaryData);
    return response.data;
  },

  bulkImportSalaries: async (rows, options = {}) => {
    const response = await api.post(
      "/salaries/bulk-import",
      {
        rows,
        ...(options.bookingPayMonth || options.payMonth
          ? { bookingPayMonth: options.bookingPayMonth || options.payMonth }
          : {}),
      },
      { timeout: 180000 }
    );
    return response.data;
  },

  getRevisionReasons: async () => {
    const response = await api.get("/salaries/revision-reasons");
    return response.data;
  },

  getSalaryHistory: async (employeeId) => {
    const response = await api.get(`/salaries/history/${employeeId}`);
    return response.data;
  },

  getSalaryDeleteImpact: async (salaryRevisionId) => {
    const response = await api.get(`/salaries/${salaryRevisionId}/delete-impact`);
    return response.data;
  },

  deleteSalaryRevision: async (salaryRevisionId) => {
    const response = await api.delete(`/salaries/${salaryRevisionId}`);
    return response.data;
  },

  getSalary: async (id) => {
    const response = await api.get(`/salaries/${id}`);
    return response.data;
  },
};

/** Payroll ops: statutory applicability history, runs, checklists */
export const payrollPolicyService = {
  listLoanPolicies: async (params = {}) => {
    const response = await api.get("/payroll-policies/loan-policies", { params });
    return response.data;
  },
  saveLoanPolicy: async (payload) => {
    const id = payload?.id;
    const response = id
      ? await api.put(`/payroll-policies/loan-policies/${id}`, payload)
      : await api.post("/payroll-policies/loan-policies", payload);
    return response.data;
  },
  deleteLoanPolicy: async (id) => {
    const response = await api.delete(`/payroll-policies/loan-policies/${id}`);
    return response.data;
  },
  listLeaveEncashmentPolicies: async (params = {}) => {
    const response = await api.get("/payroll-policies/leave-encashment-policies", { params });
    return response.data;
  },
  saveLeaveEncashmentPolicy: async (payload) => {
    const id = payload?.id;
    const response = id
      ? await api.put(`/payroll-policies/leave-encashment-policies/${id}`, payload)
      : await api.post("/payroll-policies/leave-encashment-policies", payload);
    return response.data;
  },
  deleteLeaveEncashmentPolicy: async (id) => {
    const response = await api.delete(`/payroll-policies/leave-encashment-policies/${id}`);
    return response.data;
  },
  listLoanApplications: async (params = {}) => {
    const response = await api.get("/payroll-policies/loan-applications", { params });
    return response.data;
  },
  createLoanApplication: async (payload) => {
    const response = await api.post("/payroll-policies/loan-applications", payload);
    return response.data;
  },
  listLeaveEncashmentRequests: async (params = {}) => {
    const response = await api.get("/payroll-policies/leave-encashment-requests", { params });
    return response.data;
  },
  createLeaveEncashmentRequest: async (payload) => {
    const response = await api.post("/payroll-policies/leave-encashment-requests", payload);
    return response.data;
  },
  listAwayLeaveEncashmentRegister: async (params = {}) => {
    const response = await api.get("/payroll-policies/leave-encashment-away-register", {
      params,
    });
    return response.data;
  },
};

export const payrollOpsService = {
  listStatutoryApplicability: async (employeeId) => {
    const response = await api.get(`/payroll-ops/statutory-applicability/${employeeId}`);
    return response.data;
  },
  resolveStatutoryApplicability: async (employeeId, asOf) => {
    const response = await api.get(
      `/payroll-ops/statutory-applicability/${employeeId}/resolve`,
      { params: { asOf } }
    );
    return response.data;
  },
  resolveStatutoryBatch: async (employeeIds, asOf) => {
    const response = await api.post("/payroll-ops/statutory-applicability/resolve-batch", {
      employeeIds,
      asOf,
    });
    return response.data;
  },
  createStatutoryApplicability: async (employeeId, data) => {
    const response = await api.post(
      `/payroll-ops/statutory-applicability/${employeeId}`,
      data
    );
    return response.data;
  },
  deleteStatutoryApplicability: async (employeeId, id) => {
    const response = await api.delete(
      `/payroll-ops/statutory-applicability/${employeeId}/${id}`
    );
    return response.data;
  },
  getStructureEditLock: async (employeeId) => {
    const response = await api.get(
      `/payroll-ops/employees/${employeeId}/structure-edit-lock`
    );
    return response.data;
  },
  getOrCreateRun: async (payMonth) => {
    const response = await api.get("/payroll-ops/runs", { params: { payMonth } });
    return response.data;
  },
  /** Lookup only — does not create a draft run for the month. */
  findRun: async (payMonth) => {
    const response = await api.get("/payroll-ops/runs", {
      params: { payMonth, create: false },
    });
    return response.data;
  },
  /** Pay months with finalizedAt set — for statutory filing month pickers. */
  listFinalizedPayrollMonths: async () => {
    const response = await api.get("/payroll-ops/runs/finalized-months");
    return response.data;
  },
  /** Payroll Dashboard analytics — read-only register/summary aggregates. */
  getDashboardAnalytics: async (params = {}) => {
    const response = await api.get("/payroll-ops/dashboard/analytics", {
      params: {
        periodMode: params.periodMode || "month",
        year: params.year,
        month: params.month,
        rangeFrom: params.rangeFrom || undefined,
        rangeTo: params.rangeTo || undefined,
        groupBy: params.groupBy || "department",
        comparePayMonths: Array.isArray(params.comparePayMonths)
          ? params.comparePayMonths.join(",")
          : params.comparePayMonths || undefined,
      },
      timeout: 180000,
    });
    return response.data;
  },
  updateRun: async (id, data) => {
    const response = await api.patch(`/payroll-ops/runs/${id}`, data);
    return response.data;
  },
  resolveScope: async (runId, payload) => {
    const response = await api.post(`/payroll-ops/runs/${runId}/resolve-scope`, payload);
    return response.data;
  },
  validateStep: async (runId, payload) => {
    // Declarations/TDS compares many employees — allow longer than default 60s
    const heavy = payload?.stepKey === "tax_declarations";
    const response = await api.post(`/payroll-ops/runs/${runId}/validate-step`, payload, {
      timeout: heavy ? 180000 : 60000,
    });
    return response.data;
  },
  processRun: async (id, payload = {}) => {
    // Per-employee payroll is heavy (attendance, LOP, TDS); never abandon a batch the server is still computing
    const response = await api.post(`/payroll-ops/runs/${id}/process`, payload, {
      timeout: 300000,
    });
    return response.data;
  },
  /** Live Process/Reprocess/Unprocess lock for a run: null when free. */
  getProcessingLock: async (id) => {
    const response = await api.get(`/payroll-ops/runs/${id}/processing-lock`, { timeout: 15000 });
    return response.data?.data || null;
  },
  resetRun: async (id, payload = {}) => {
    const response = await api.post(`/payroll-ops/runs/${id}/reset`, payload);
    return response.data;
  },
  getRunEmployees: async (runId) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/employees`);
    return response.data;
  },
  getSalaryRegister: async (runId, payMonth = null, options = {}) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/salary-register`, {
      params: {
        ...(payMonth ? { payMonth } : {}),
        jvGroupBy: options.jvGroupBy || "all",
      },
      timeout: 180000,
    });
    return response.data;
  },
  /** ESI IPs relieved in previous month (for current MC return, reason code 2). */
  listEsicPriorMonthExits: async (payMonth) => {
    const response = await api.get("/payroll-ops/esic-prior-month-exits", {
      params: { payMonth: String(payMonth || "").slice(0, 7) },
    });
    return response.data;
  },
  /** Fill official ESIC MC_Template11.xls (formatting preserved) and return .xls blob. */
  downloadEsicMcTemplate: async (rows, fileName = "MC_Template11.xls") => {
    const response = await api.post(
      "/payroll-ops/esic-mc-template",
      { rows, fileName },
      {
        responseType: "blob",
        timeout: 180000,
      }
    );
    return response.data;
  },
  getPayrollProjectionReport: async (runId, payMonth = null) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/reports/payroll-projection`, {
      params: payMonth ? { payMonth } : {},
      timeout: 180000,
    });
    return response.data;
  },
  getPayrollRegisterReport: async (runId, payMonth = null, options = {}) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/reports/payroll-register`, {
      params: {
        ...(payMonth ? { payMonth } : {}),
        jvGroupBy: options.jvGroupBy || "all",
      },
      timeout: 180000,
    });
    return response.data;
  },
  getPayrollReconciliationMeta: async (runId, payMonth = null) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/reports/payroll-reconciliation`, {
      params: { ...(payMonth ? { payMonth } : {}), metaOnly: 1 },
      timeout: 60000,
    });
    return response.data;
  },
  getPayrollReconciliationReport: async (runId, payMonth = null, comparePayMonths = []) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/reports/payroll-reconciliation`, {
      params: {
        ...(payMonth ? { payMonth } : {}),
        comparePayMonths: (comparePayMonths || []).join(","),
      },
      timeout: 180000,
    });
    return response.data;
  },
  getAccountingJournalReport: async (runId, payMonth = null, options = {}) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/reports/accounting-journal`, {
      params: {
        ...(payMonth ? { payMonth } : {}),
        jvGroupBy: options.jvGroupBy || "all",
      },
      timeout: 180000,
    });
    return response.data;
  },
  listMonthlyInputs: async (payMonth, employeeId = null) => {
    const response = await api.get("/payroll-ops/monthly-inputs", {
      params: { payMonth, employeeId },
    });
    return response.data;
  },
  listRecurrentComponents: async () => {
    const response = await api.get("/payroll-ops/recurrent-components");
    return response.data;
  },
  createMonthlyInput: async (data) => {
    const response = await api.post("/payroll-ops/monthly-inputs", data);
    return response.data;
  },
  updateMonthlyInput: async (id, data) => {
    const response = await api.put(`/payroll-ops/monthly-inputs/${id}`, data);
    return response.data;
  },
  deleteMonthlyInput: async (id) => {
    const response = await api.delete(`/payroll-ops/monthly-inputs/${id}`);
    return response.data;
  },
  headcountCompare: async (payload) => {
    const response = await api.post("/payroll-ops/headcount-compare", payload);
    return response.data;
  },
  checklistLinkCatalog: async () => {
    const response = await api.get("/payroll-ops/checklist-link-catalog");
    return response.data;
  },
  listChecklists: async (payMonth = null) => {
    const response = await api.get("/payroll-ops/checklists", {
      params: payMonth ? { payMonth } : {},
    });
    return response.data;
  },
  createChecklist: async (data) => {
    const response = await api.post("/payroll-ops/checklists", data);
    return response.data;
  },
  updateChecklist: async (id, data) => {
    const response = await api.put(`/payroll-ops/checklists/${id}`, data);
    return response.data;
  },
  deleteChecklist: async (id) => {
    const response = await api.delete(`/payroll-ops/checklists/${id}`);
    return response.data;
  },
  getChecklistCompletions: async (payMonth, checklistId) => {
    const response = await api.get("/payroll-ops/checklist-completions", {
      params: { payMonth, checklistId },
    });
    return response.data;
  },
  upsertChecklistCompletion: async (data) => {
    const response = await api.post("/payroll-ops/checklist-completions", data);
    return response.data;
  },
  finaliseRun: async (id) => {
    const response = await api.post(`/payroll-ops/runs/${id}/finalise`);
    return response.data;
  },
  listFinaliseAudits: async (params = {}) => {
    const response = await api.get("/payroll-ops/finalise-audits", { params });
    return response.data;
  },
  requestUnlockRun: async (id) => {
    const response = await api.post(`/payroll-ops/runs/${id}/unlock-request`);
    return response.data;
  },
  approveUnlockRun: async (id, approve = true) => {
    const response = await api.post(`/payroll-ops/runs/${id}/unlock-approve`, { approve });
    return response.data;
  },
  listDisbursements: async (payMonth) => {
    const response = await api.get("/payroll-ops/disbursements", { params: { payMonth } });
    return response.data;
  },
  applyDisbursement: async (data) => {
    const response = await api.post("/payroll-ops/disbursements/apply", data);
    return response.data;
  },
  listPaymentLedger: async (params = {}) => {
    const response = await api.get("/payroll-ops/payment-ledger", { params });
    return response.data;
  },
  getUnpaidSalaryReport: async (params = {}) => {
    const response = await api.get("/payroll-ops/reports/unpaid-salary", { params });
    return response.data;
  },
  listActionRecords: async (actionKey, params = {}) => {
    const response = await api.get(`/payroll-ops/actions/${encodeURIComponent(actionKey)}/records`, {
      params,
    });
    return response.data;
  },
  getStructureAsOfReport: async (params = {}) => {
    const response = await api.get("/payroll-ops/reports/structure-as-of", { params });
    return response.data;
  },
  listFnfSettlements: async (params = {}) => {
    const response = await api.get("/payroll-ops/fnf/settlements", { params });
    return response.data;
  },
  listFnfEligibleEmployees: async (params = {}) => {
    const response = await api.get("/payroll-ops/fnf/eligible-employees", { params });
    return response.data;
  },
  deleteFnfSettlement: async (id) => {
    const response = await api.delete(`/payroll-ops/fnf/settlements/${id}`);
    return response.data;
  },
  getFnfSettlement: async (employeeId, payMonth, opts = {}) => {
    const response = await api.get(`/payroll-ops/fnf/${employeeId}`, {
      params: {
        payMonth,
        ...(opts.includeLeavingSalary != null
          ? { includeLeavingSalary: opts.includeLeavingSalary ? "true" : "false" }
          : {}),
      },
    });
    return response.data;
  },
  recalculateFnfSettlement: async (employeeId, payMonthOrBody) => {
    const body =
      typeof payMonthOrBody === "string"
        ? { payMonth: payMonthOrBody }
        : payMonthOrBody || {};
    const response = await api.post(`/payroll-ops/fnf/${employeeId}/recalculate`, body);
    return response.data;
  },
  updateFnfSettlement: async (employeeId, data) => {
    const response = await api.put(`/payroll-ops/fnf/${employeeId}`, data);
    return response.data;
  },
  releasePaymentHold: async (id, payMonth) => {
    const response = await api.post(`/payroll-ops/payment-holds/${id}/release`, { payMonth });
    return response.data;
  },
  listPayslipTemplates: async () => {
    const response = await api.get("/payroll-ops/payslip-templates");
    return response.data;
  },
  previewPayslipTemplatePdf: async (data = {}) => {
    const response = await api.post("/payroll-ops/payslip-templates/preview-pdf", data, {
      responseType: "blob",
    });
    return response.data;
  },
  createPayslipTemplate: async (data) => {
    const response = await api.post("/payroll-ops/payslip-templates", data);
    return response.data;
  },
  updatePayslipTemplate: async (id, data) => {
    const response = await api.put(`/payroll-ops/payslip-templates/${id}`, data);
    return response.data;
  },
  publishPayslips: async (runId, data = {}) => {
    const response = await api.post(`/payroll-ops/runs/${runId}/publish-payslips`, data, {
      timeout: 180000,
    });
    return response.data;
  },
  unpublishPayslips: async (runId, data = {}) => {
    const response = await api.post(`/payroll-ops/runs/${runId}/unpublish-payslips`, data, {
      timeout: 180000,
    });
    return response.data;
  },
  listRunPayslips: async (runId) => {
    const response = await api.get(`/payroll-ops/runs/${runId}/payslips`);
    return response.data;
  },
  getStepCatalog: async () => {
    const response = await api.get("/payroll-ops/step-catalog");
    return response.data;
  },
  notifyPendingFields: async (data) => {
    const response = await api.post("/payroll-ops/notify-pending-fields", data);
    return response.data;
  },
  bulkCreateMonthlyInputs: async (data) => {
    const response = await api.post("/payroll-ops/monthly-inputs/bulk", data);
    return response.data;
  },
  listEmployeeBankAccounts: async (employeeId) => {
    const response = await api.get(`/payroll-ops/bank-accounts/${employeeId}`);
    return response.data;
  },
  getBankChangePreview: async (employeeId, params = {}) => {
    const response = await api.get(`/payroll-ops/bank-accounts/${employeeId}/change-preview`, {
      params,
    });
    return response.data;
  },
  createEmployeeBankAccount: async (employeeId, data) => {
    const response = await api.post(`/payroll-ops/bank-accounts/${employeeId}`, data);
    return response.data;
  },
  deleteEmployeeBankAccount: async (employeeId, id) => {
    const response = await api.delete(`/payroll-ops/bank-accounts/${employeeId}/${id}`);
    return response.data;
  },
  overrideTds: async (employeeId, data) => {
    const response = await api.post(`/payroll-ops/tds/${employeeId}/override`, data);
    return response.data;
  },
  bulkOverrideTds: async (data) => {
    const response = await api.post("/payroll-ops/tds/bulk-override", data);
    return response.data;
  },
  clearTdsOverride: async (employeeId, payMonth, extra = {}) => {
    const response = await api.delete(`/payroll-ops/tds/${employeeId}/override`, {
      data: { payMonth, ...extra },
    });
    return response.data;
  },
  getTaxLiability: async (employeeId, payMonth) => {
    const response = await api.get(`/payroll-ops/tds/${employeeId}/liability`, {
      params: payMonth ? { payMonth } : {},
    });
    return response.data;
  },
  listOneTimeTax: async (payMonth) => {
    const response = await api.get("/payroll-ops/tds/one-time-tax", {
      params: { payMonth },
    });
    return response.data;
  },
  exportTdsComputationWorkbook: async ({ payMonth }) => {
    const response = await api.post(
      "/payroll-ops/tds/computation-export",
      { payMonth },
      { responseType: "blob", timeout: 600000 }
    );
    return response;
  },
  getMyPayrollSummary: async ({ payMonth, view } = {}) => {
    const response = await api.get("/payroll-ops/my-payroll-summary", {
      params: {
        ...(payMonth ? { payMonth } : {}),
        ...(view ? { view } : {}),
      },
    });
    return response.data;
  },
  getForm16: async (employeeId, financialYear) => {
    const response = await api.get(`/payroll-ops/form16/${employeeId}`, {
      params: { financialYear },
    });
    return response.data;
  },
  getForm24Q: async (financialYear, quarter = "Q1") => {
    const response = await api.get("/payroll-ops/form24q", {
      params: { financialYear, quarter },
    });
    return response.data;
  },
  getFvuCatalog: async (financialYear, quarter) => {
    const response = await api.get("/payroll-ops/form24q/fvu-catalog", {
      params: financialYear && quarter ? { financialYear, quarter } : {},
    });
    return response.data;
  },
  validateForm24QFiling: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/validate", payload);
    return response.data;
  },
  exportForm24QRpu: async (params) => {
    const response = await api.get("/payroll-ops/form24q/export-rpu", {
      params,
      responseType: "blob",
    });
    return response;
  },
  exportForm138Excel: async (params) => {
    const response = await api.get("/payroll-ops/form24q/export-excel", {
      params,
      responseType: "blob",
    });
    return response;
  },
  listTdsFilings: async (financialYear) => {
    const response = await api.get("/payroll-ops/form24q/filings", {
      params: financialYear ? { financialYear } : {},
    });
    return response.data;
  },
  prepareTdsFiling: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/prepare", payload);
    return response.data;
  },
  getForm24QPortPreview: async (params = {}) => {
    const response = await api.get("/payroll-ops/form24q/port-preview", { params });
    return response.data;
  },
  calculateForm24QPortData: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/port-data/calculate", payload);
    return response.data;
  },
  saveForm24QPortData: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/port-data", payload);
    return response.data;
  },
  fileTdsReturn: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/file", payload);
    return response.data;
  },
  getForm24QFilingBundle: async (filingId) => {
    const response = await api.get(`/payroll-ops/form24q/filings/${filingId}/bundle`);
    return response.data;
  },
  upsertForm24QChallan: async (filingId, payload, challanId = null) => {
    const url = challanId
      ? `/payroll-ops/form24q/filings/${filingId}/challans/${challanId}`
      : `/payroll-ops/form24q/filings/${filingId}/challans`;
    const response = challanId
      ? await api.put(url, payload)
      : await api.post(url, payload);
    return response.data;
  },
  deleteForm24QChallan: async (filingId, challanId) => {
    const response = await api.delete(
      `/payroll-ops/form24q/filings/${filingId}/challans/${challanId}`
    );
    return response.data;
  },
  saveForm24QMappings: async (filingId, mappings) => {
    const response = await api.put(`/payroll-ops/form24q/filings/${filingId}/mappings`, {
      mappings,
    });
    return response.data;
  },
  importForm24QCsi: async (filingId, { content, fileName }) => {
    const response = await api.post(`/payroll-ops/form24q/filings/${filingId}/import-csi`, {
      content,
      fileName,
    });
    return response.data;
  },
  validateForm24QReview: async (filingId) => {
    const response = await api.post(
      `/payroll-ops/form24q/filings/${filingId}/validate-review`
    );
    return response.data;
  },
  runForm24QFvu: async (filingId) => {
    // In local CRA/craco, long FVU runs often die via the webpack proxy (ECONNRESET),
    // especially if nodemon restarts when the jar writes fvu.log. Call the API host directly.
    const proxyTarget = (
      process.env.REACT_APP_PROXY_TARGET ||
      (process.env.NODE_ENV === "development" ? "http://127.0.0.1:3001" : "")
    ).replace(/\/$/, "");
    const directBase =
      process.env.NODE_ENV === "development" && proxyTarget
        ? `${proxyTarget}/api/`
        : undefined;
    const response = await api.post(
      `/payroll-ops/form24q/filings/${filingId}/run-fvu`,
      {},
      {
        timeout: 600000,
        ...(directBase ? { baseURL: directBase } : {}),
      }
    );
    return response.data;
  },
  listForm24QArtifacts: async (filingId) => {
    const response = await api.get(`/payroll-ops/form24q/filings/${filingId}/artifacts`);
    return response.data;
  },
  downloadForm24QArtifactUrl: (filingId, artifactId) =>
    `/payroll-ops/form24q/filings/${filingId}/artifacts/${artifactId}/download`,
  downloadForm24QArtifact: async (filingId, artifactId) => {
    const response = await api.get(
      `/payroll-ops/form24q/filings/${filingId}/artifacts/${artifactId}/download`,
      { responseType: "blob" }
    );
    return response;
  },
  getTdsEngineStatus: async () => {
    const response = await api.get("/payroll-ops/tds-engine/versions");
    return response.data;
  },
  getTdsSalaryRegimes: async () => {
    const response = await api.get("/payroll-ops/tds-engine/salary-regimes");
    return response.data;
  },
  installTdsSalaryRegime: async (formData) => {
    const response = await api.post("/payroll-ops/tds-engine/salary-regimes/install", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    });
    return response.data;
  },
  resetTdsSalaryEngine: async (payload = {}) => {
    const response = await api.post("/payroll-ops/tds-engine/salary-regimes/reset", payload);
    return response.data;
  },
  registerTdsEngineVersion: async (payload) => {
    const response = await api.post("/payroll-ops/tds-engine/versions", payload);
    return response.data;
  },
  activateTdsEngineVersion: async (id, payload = { active: true }) => {
    const response = await api.put(`/payroll-ops/tds-engine/versions/${id}/activate`, payload);
    return response.data;
  },
  uploadTdsFvuPack: async (formData) => {
    const response = await api.post("/payroll-ops/tds-engine/upload-fvu", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  uploadTdsSpecPack: async (formData) => {
    const response = await api.post("/payroll-ops/tds-engine/upload-spec", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  startForm24QCorrection: async (payload) => {
    const response = await api.post("/payroll-ops/form24q/correction", payload);
    return response.data;
  },
  getDeductorMaster: async () => {
    const response = await api.get("/payroll-ops/deductor-master");
    return response.data;
  },
  updateDeductorMaster: async (data) => {
    const response = await api.put("/payroll-ops/deductor-master", data);
    return response.data;
  },
  prefillResponsiblePerson: async (employeeId) => {
    const response = await api.get(`/payroll-ops/deductor-master/prefill-rp/${employeeId}`);
    return response.data;
  },
  saveDeductorQuarterOverride: async (filingId, data) => {
    const response = await api.put(`/payroll-ops/form24q/filings/${filingId}/deductor-override`, data);
    return response.data;
  },
  getEmployees: async () => {
    const response = await api.get("/employees");
    return response.data;
  },
  listTdsChallans: async (params = {}) => {
    const response = await api.get("/payroll-ops/tds-challans", { params });
    return response.data;
  },
  upsertTdsChallanMaster: async (data, challanId = null) => {
    const response = challanId
      ? await api.put(`/payroll-ops/tds-challans/${challanId}`, data)
      : await api.post("/payroll-ops/tds-challans", data);
    return response.data;
  },
  cancelTdsChallan: async (challanId) => {
    const response = await api.post(`/payroll-ops/tds-challans/${challanId}/cancel`);
    return response.data;
  },
  getTdsChallanAvailable: async (params = {}) => {
    const response = await api.get("/payroll-ops/tds-challans/available", { params });
    return response.data;
  },
  getTdsLinkableDeductions: async (params = {}) => {
    const response = await api.get("/payroll-ops/tds-challans/linkable-deductions", { params });
    return response.data;
  },
  linkTdsChallanDeductions: async (challanId, payload) => {
    const response = await api.post(`/payroll-ops/tds-challans/${challanId}/link`, payload);
    return response.data;
  },
  getTdsChallanConsumptions: async (params = {}) => {
    const response = await api.get("/payroll-ops/tds-challans/consumptions", { params });
    return response.data;
  },
  reverseTdsChallanConsumption: async (consumptionId, remarks) => {
    const response = await api.post(
      `/payroll-ops/tds-challans/consumptions/${consumptionId}/reverse`,
      { remarks }
    );
    return response.data;
  },
};

// Resignation services
export const resignationService = {
  createResignation: async (resignationData) => {
    const response = await api.post("/resignations", resignationData);
    return response.data;
  },

  getResignationHistory: async (employeeId) => {
    const response = await api.get(`/resignations/history/${employeeId}`);
    return response.data;
  },

  getResignation: async (id) => {
    const response = await api.get(`/resignations/${id}`);
    return response.data;
  },
};

export const issuedLetterService = {
  listForEmployee: async (employeeId) => {
    const response = await api.get(`/issued-letters/employee/${employeeId}`);
    return response.data;
  },
  persistGenerated: async (payload) => {
    const response = await api.post("/issued-letters/persist", payload);
    return response.data;
  },
  uploadForEmployee: async (employeeId, formData) => {
    const response = await api.post(
      `/issued-letters/employee/${employeeId}/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },
  bulkUpload: async (formData) => {
    const response = await api.post("/issued-letters/bulk", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  getUrl: async (letterId) => {
    const response = await api.get(`/issued-letters/${letterId}/url`);
    return response.data;
  },
  getDownloadUrl: async (letterId) => {
    const response = await api.get(`/issued-letters/${letterId}/download-url`);
    return response.data;
  },
  requestSignOtp: async (letterId) => {
    const response = await api.post(`/issued-letters/${letterId}/sign/request-otp`);
    return response.data;
  },
  verifySignOtp: async (letterId, otp) => {
    const response = await api.post(`/issued-letters/${letterId}/sign/verify`, { otp });
    return response.data;
  },
};

export const letterTemplateService = {
  list: async (params = {}) => {
    const response = await api.get("/letter-templates", { params });
    return response.data;
  },
  save: async (data, file) => {
    if (file) {
      const form = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        if (value == null) return;
        if (typeof value === "object") {
          form.append(key, JSON.stringify(value));
        } else {
          form.append(key, String(value));
        }
      });
      form.append("file", file);
      const response = await api.post("/letter-templates", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    }
    const response = await api.post("/letter-templates", data);
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/letter-templates/${id}`);
    return response.data;
  },
  downloadFile: async (id) => {
    const response = await api.get(`/letter-templates/${id}/file`, {
      responseType: "blob",
    });
    return response.data;
  },
  sendLetter: async (payload) => {
    const response = await api.post("/letter-templates/send-letter", payload);
    return response.data;
  },
  convertToPdf: async ({ docxBase64, fileName }) => {
    const response = await api.post(
      "/letter-templates/convert-to-pdf",
      { docxBase64, fileName },
      { responseType: "blob", timeout: 120000 }
    );
    return response.data;
  },
};

export const helpdeskService = {
  getConfig: async () => {
    const response = await api.get("/helpdesk/config");
    return response.data;
  },
  saveConfig: async (config) => {
    const response = await api.put("/helpdesk/config", { config });
    return response.data;
  },
  listTickets: async (params = {}) => {
    const response = await api.get("/helpdesk/tickets", { params });
    return response.data;
  },
  listDeskTickets: async () => {
    const response = await api.get("/helpdesk/tickets/desk");
    return response.data;
  },
  createTicket: async (payload) => {
    const response = await api.post("/helpdesk/tickets", payload);
    return response.data;
  },
  updateTicket: async (id, payload) => {
    const response = await api.put(`/helpdesk/tickets/${id}`, payload);
    return response.data;
  },
  deleteTicket: async (id) => {
    const response = await api.delete(`/helpdesk/tickets/${id}`);
    return response.data;
  },
  addComment: async (id, comment) => {
    const response = await api.post(`/helpdesk/tickets/${id}/comments`, {
      comment,
    });
    return response.data;
  },
  assignTicket: async (id, payload) => {
    const response = await api.post(`/helpdesk/tickets/${id}/assign`, payload);
    return response.data;
  },
  delegateTicket: async (id, payload) => {
    const response = await api.post(`/helpdesk/tickets/${id}/delegate`, payload);
    return response.data;
  },
  remindTicket: async (id, note) => {
    const response = await api.post(`/helpdesk/tickets/${id}/remind`, {
      note,
      reminderNote: note,
    });
    return response.data;
  },
  closeTicket: async (id, payload = {}) => {
    const response = await api.post(`/helpdesk/tickets/${id}/close`, payload);
    return response.data;
  },
  submitFeedback: async (id, payload) => {
    const response = await api.post(`/helpdesk/tickets/${id}/feedback`, payload);
    return response.data;
  },
  getFeedbackReport: async () => {
    const response = await api.get("/helpdesk/reports/feedback");
    return response.data;
  },
  listAgents: async () => {
    const response = await api.get("/helpdesk/agents");
    return response.data;
  },
};

export const assetService = {
  getConfig: async () => {
    const response = await api.get("/assets/config");
    return response.data;
  },
  saveConfig: async (config) => {
    const response = await api.put("/assets/config", { config });
    return response.data;
  },
  listCategories: async (params = {}) => {
    const response = await api.get("/assets/categories", { params });
    return response.data;
  },
  createCategory: async (payload) => {
    const response = await api.post("/assets/categories", payload);
    return response.data;
  },
  updateCategory: async (id, payload) => {
    const response = await api.put(`/assets/categories/${id}`, payload);
    return response.data;
  },
  deleteCategory: async (id) => {
    const response = await api.delete(`/assets/categories/${id}`);
    return response.data;
  },
  listTypes: async (params = {}) => {
    const response = await api.get("/assets/types", { params });
    return response.data;
  },
  createType: async (payload) => {
    const response = await api.post("/assets/types", payload);
    return response.data;
  },
  updateType: async (id, payload) => {
    const response = await api.put(`/assets/types/${id}`, payload);
    return response.data;
  },
  deleteType: async (id) => {
    const response = await api.delete(`/assets/types/${id}`);
    return response.data;
  },
  getDashboard: async (params = {}) => {
    const response = await api.get("/assets/dashboard", { params });
    return response.data;
  },
  listAssets: async (params = {}) => {
    const response = await api.get("/assets/assets", { params });
    return response.data;
  },
  getAsset: async (id) => {
    const response = await api.get(`/assets/assets/${id}`);
    return response.data;
  },
  createAsset: async (payload) => {
    const response = await api.post("/assets/assets", payload);
    return response.data;
  },
  updateAsset: async (id, payload) => {
    const response = await api.put(`/assets/assets/${id}`, payload);
    return response.data;
  },
  deleteAsset: async (id) => {
    const response = await api.delete(`/assets/assets/${id}`);
    return response.data;
  },
  getAssetHistory: async (id, params = {}) => {
    const response = await api.get(`/assets/assets/${id}/history`, { params });
    return response.data;
  },
  assignAsset: async (id, payload) => {
    const response = await api.post(`/assets/assets/${id}/assign`, payload);
    return response.data;
  },
  returnAsset: async (id, payload) => {
    const response = await api.post(`/assets/assets/${id}/return`, payload);
    return response.data;
  },
  transferAsset: async (id, payload) => {
    const response = await api.post(`/assets/assets/${id}/transfer`, payload);
    return response.data;
  },
  adjustStock: async (id, payload) => {
    const response = await api.post(`/assets/assets/${id}/adjust-stock`, payload);
    return response.data;
  },
  startMaintenance: async (id, payload) => {
    const response = await api.post(`/assets/assets/${id}/maintenance`, payload);
    return response.data;
  },
  completeMaintenance: async (maintenanceId, payload = {}) => {
    const response = await api.post(
      `/assets/maintenance/${maintenanceId}/complete`,
      payload
    );
    return response.data;
  },
  listRequests: async (params = {}) => {
    const response = await api.get("/assets/requests", { params });
    return response.data;
  },
  createRequest: async (payload) => {
    const response = await api.post("/assets/requests", payload);
    return response.data;
  },
  approveRequest: async (id, payload = {}) => {
    const response = await api.post(`/assets/requests/${id}/approve`, payload);
    return response.data;
  },
  rejectRequest: async (id, payload = {}) => {
    const response = await api.post(`/assets/requests/${id}/reject`, payload);
    return response.data;
  },
  reserveRequest: async (id, payload = {}) => {
    const response = await api.post(`/assets/requests/${id}/reserve`, payload);
    return response.data;
  },
  assignRequest: async (id, payload = {}) => {
    const response = await api.post(`/assets/requests/${id}/assign`, payload);
    return response.data;
  },
  cancelRequest: async (id, payload = {}) => {
    const response = await api.post(`/assets/requests/${id}/cancel`, payload);
    return response.data;
  },
  listMyAssets: async (params = {}) => {
    const response = await api.get("/assets/my/assets", { params });
    return response.data;
  },
  listMyRequests: async (params = {}) => {
    const response = await api.get("/assets/my/requests", { params });
    return response.data;
  },
  createMyRequest: async (payload) => {
    const response = await api.post("/assets/my/requests", payload);
    return response.data;
  },
  listStock: async (params = {}) => {
    const response = await api.get("/assets/operations/stock", { params });
    return response.data;
  },
  listVerifications: async (params = {}) => {
    const response = await api.get("/assets/verifications", { params });
    return response.data;
  },
  createVerification: async (payload) => {
    const response = await api.post("/assets/verifications", payload);
    return response.data;
  },
  updateVerificationItems: async (id, payload) => {
    const response = await api.post(`/assets/verifications/${id}/items`, payload);
    return response.data;
  },
  completeVerification: async (id, payload = {}) => {
    const response = await api.post(
      `/assets/verifications/${id}/complete`,
      payload
    );
    return response.data;
  },
  listScrap: async (params = {}) => {
    const response = await api.get("/assets/scrap", { params });
    return response.data;
  },
  createScrap: async (payload) => {
    const response = await api.post("/assets/scrap", payload);
    return response.data;
  },
  approveScrap: async (id, payload = {}) => {
    const response = await api.post(`/assets/scrap/${id}/approve`, payload);
    return response.data;
  },
  rejectScrap: async (id, payload = {}) => {
    const response = await api.post(`/assets/scrap/${id}/reject`, payload);
    return response.data;
  },
  disposeScrap: async (id, payload = {}) => {
    const response = await api.post(`/assets/scrap/${id}/dispose`, payload);
    return response.data;
  },
  listRecoveries: async (params = {}) => {
    const response = await api.get("/assets/recoveries", { params });
    return response.data;
  },
  updateRecovery: async (id, payload) => {
    const response = await api.put(`/assets/recoveries/${id}`, payload);
    return response.data;
  },
  approveRecovery: async (id, payload = {}) => {
    const response = await api.post(`/assets/recoveries/${id}/approve`, payload);
    return response.data;
  },
  waiveRecovery: async (id, payload = {}) => {
    const response = await api.post(`/assets/recoveries/${id}/waive`, payload);
    return response.data;
  },
  sendRecoveryToFnf: async (id, payload = {}) => {
    const response = await api.post(`/assets/recoveries/${id}/send-fnf`, payload);
    return response.data;
  },
  getEmployeeOutstanding: async (employeeId) => {
    const response = await api.get(`/assets/employee/${employeeId}/outstanding`);
    return response.data;
  },
  getReport: async (reportKey, params = {}) => {
    const response = await api.get(`/assets/reports/${reportKey}`, { params });
    return response.data;
  },
  importData: async (importKey, payload) => {
    const body =
      payload instanceof FormData
        ? payload
        : { rows: Array.isArray(payload?.rows) ? payload.rows : payload };
    const response = await api.post(`/assets/import/${importKey}`, body);
    return response.data;
  },
  exportData: async (exportKey, params = {}) => {
    const response = await api.get(`/assets/export/${exportKey}`, { params });
    return response.data;
  },
  getTemplate: async (templateKey) => {
    const response = await api.get(`/assets/templates/${templateKey}`);
    return response.data;
  },
  listDesk: async (params = {}) => {
    const response = await api.get("/assets/desk", { params });
    return response.data;
  },
};

// Attendance Structure services
export const attendanceStructureService = {
  // Weekly Off Policies
  getWeeklyOffPolicies: async () => {
    const response = await api.get("/attendance-structure/weekly-off-policies");
    return response.data;
  },

  createWeeklyOffPolicy: async (policyData) => {
    const response = await api.post(
      "/attendance-structure/weekly-off-policies",
      policyData
    );
    return response.data;
  },

  updateWeeklyOffPolicy: async (id, policyData) => {
    const response = await api.put(
      `/attendance-structure/weekly-off-policies/${id}`,
      policyData
    );
    return response.data;
  },

  previewWeeklyOffPolicyImpact: async (id, policyData) => {
    const response = await api.post(
      `/attendance-structure/weekly-off-policies/${id}/impact`,
      policyData
    );
    return response.data;
  },

  deleteWeeklyOffPolicy: async (id) => {
    const response = await api.delete(
      `/attendance-structure/weekly-off-policies/${id}`
    );
    return response.data;
  },

  // Attendance process policies (auto-punch / manual override)
  getAttendancePolicies: async () => {
    const response = await api.get("/attendance-structure/attendance-policies");
    return response.data;
  },

  createAttendancePolicy: async (policyData) => {
    const response = await api.post(
      "/attendance-structure/attendance-policies",
      policyData
    );
    return response.data;
  },

  updateAttendancePolicy: async (id, policyData) => {
    const response = await api.put(
      `/attendance-structure/attendance-policies/${id}`,
      policyData
    );
    return response.data;
  },

  deleteAttendancePolicy: async (id, opts = {}) => {
    const response = await api.delete(
      `/attendance-structure/attendance-policies/${id}`,
      { params: opts?.soft ? { soft: 1 } : undefined }
    );
    return response.data;
  },

  getAttendanceShifts: async () => {
    const response = await api.get("/attendance-structure/attendance-shifts");
    return response.data;
  },

  createAttendanceShift: async (shiftData) => {
    const response = await api.post(
      "/attendance-structure/attendance-shifts",
      shiftData
    );
    return response.data;
  },

  updateAttendanceShift: async (code, shiftData) => {
    const response = await api.put(
      `/attendance-structure/attendance-shifts/${encodeURIComponent(code)}`,
      shiftData
    );
    return response.data;
  },

  deleteAttendanceShift: async (code) => {
    const response = await api.delete(
      `/attendance-structure/attendance-shifts/${encodeURIComponent(code)}`
    );
    return response.data;
  },

  // Holiday Policies
  getHolidayPolicies: async () => {
    const response = await api.get("/attendance-structure/holiday-policies");
    return response.data;
  },

  createHolidayPolicy: async (policyData) => {
    const response = await api.post(
      "/attendance-structure/holiday-policies",
      policyData
    );
    return response.data;
  },

  updateHolidayPolicy: async (id, policyData) => {
    const response = await api.put(
      `/attendance-structure/holiday-policies/${id}`,
      policyData
    );
    return response.data;
  },

  deleteHolidayPolicy: async (id) => {
    const response = await api.delete(
      `/attendance-structure/holiday-policies/${id}`
    );
    return response.data;
  },

  // Holidays
  getHolidays: async () => {
    const response = await api.get("/attendance-structure/holidays");
    return response.data;
  },

  createHoliday: async (holidayData) => {
    const response = await api.post(
      "/attendance-structure/holidays",
      holidayData
    );
    return response.data;
  },

  // Holiday Bank
  getHolidayBank: async () => {
    const response = await api.get("/attendance-structure/holiday-bank");
    return response.data;
  },

  createHolidayBank: async (holidayData) => {
    const response = await api.post(
      "/attendance-structure/holiday-bank",
      holidayData
    );
    return response.data;
  },

  updateHolidayBank: async (id, holidayData) => {
    const response = await api.put(
      `/attendance-structure/holiday-bank/${id}`,
      holidayData
    );
    return response.data;
  },

  deleteHolidayBank: async (id) => {
    const response = await api.delete(
      `/attendance-structure/holiday-bank/${id}`
    );
    return response.data;
  },

  // Leave Types
  getLeaveTypes: async () => {
    const response = await api.get("/attendance-structure/leave-types");
    return response.data;
  },

  createLeaveType: async (leaveTypeData) => {
    const response = await api.post(
      "/attendance-structure/leave-types",
      leaveTypeData
    );
    return response.data;
  },

  updateLeaveType: async (id, leaveTypeData) => {
    const response = await api.put(
      `/attendance-structure/leave-types/${id}`,
      leaveTypeData
    );
    return response.data;
  },

  deleteLeaveType: async (id) => {
    const response = await api.delete(
      `/attendance-structure/leave-types/${id}`
    );
    return response.data;
  },

  // Leave Groups
  getLeaveGroups: async () => {
    const response = await api.get("/attendance-structure/leave-groups");
    return response.data;
  },

  getLeaveGroup: async (id) => {
    const response = await api.get(`/attendance-structure/leave-groups/${id}`);
    return response.data;
  },

  createLeaveGroup: async (leaveGroupData) => {
    const response = await api.post(
      "/attendance-structure/leave-groups",
      leaveGroupData
    );
    return response.data;
  },

  updateLeaveGroup: async (id, leaveGroupData) => {
    const response = await api.put(
      `/attendance-structure/leave-groups/${id}`,
      leaveGroupData
    );
    return response.data;
  },

  deleteLeaveGroup: async (id) => {
    const response = await api.delete(
      `/attendance-structure/leave-groups/${id}`
    );
    return response.data;
  },

  // Leave Adjustment: create adjustment and fetch history
  createLeaveAdjustment: async (payload) => {
    const response = await api.post(
      "/attendance-structure/leave-adjustments",
      payload
    );
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to save leave adjustment");
    }
    return data;
  },
  getLeaveAdjustmentHistory: async (employeeId) => {
    const response = await api.get("/attendance-structure/leave-adjustments", {
      params: { employeeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load adjustment history");
    }
    return data;
  },

  // All company leave types applicable to this employee (gender-filtered)
  getEmployeeLeaveTypes: async (employeeId) => {
    const response = await api.get("/attendance-structure/employee-leave-types", {
      params: { employeeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load employee leave types");
    }
    return data;
  },

  // Leave Balance: fetch balance summary for employee, year, leave type
  getLeaveBalance: async (employeeId, year, leaveTypeId) => {
    const response = await api.get("/attendance-structure/leave-balance", {
      params: { employeeId, year, leaveTypeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load leave balance");
    }
    return data;
  },

  getLeaveOpeningBalancesForGroup: async (leaveGroupId, year) => {
    const response = await api.get("/attendance-structure/leave-opening-balance", {
      params: { leaveGroupId, year },
    });
    return response.data;
  },

  // Upload leave opening balance for a leave group
  uploadLeaveOpeningBalance: async (leaveGroupId, entries, year) => {
    const response = await api.post(
      "/attendance-structure/leave-opening-balance/upload",
      {
        leaveGroupId,
        entries,
        year,
      }
    );
    return response.data;
  },

  getLeaveGroupEmployees: async (leaveGroupId) => {
    const response = await api.get(
      `/attendance-structure/leave-groups/${leaveGroupId}/employees`
    );
    return response.data;
  },

  cleanupLeaveOpeningBalanceByGender: async (leaveGroupId, year) => {
    const response = await api.post(
      "/attendance-structure/leave-opening-balance/cleanup-gender",
      { leaveGroupId, year }
    );
    return response.data;
  },

  createLeaveRequest: async (payload) => {
    const response = await api.post("/attendance-structure/leave-requests", payload);
    return response.data;
  },

  getLeaveRequests: async (params = {}) => {
    const response = await api.get("/attendance-structure/leave-requests", { params });
    return response.data;
  },

  getPendingLeaveRequestsForUser: async (params = {}) => {
    const response = await api.get(
      "/attendance-structure/leave-requests/pending-my-action",
      { params }
    );
    return response.data;
  },

  getLeaveApprovalsDesk: async (params = {}) => {
    const response = await api.get(
      "/attendance-structure/leave-requests/approvals-desk",
      { params }
    );
    return response.data;
  },

  approveLeaveRequest: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/approve`,
      { comments }
    );
    return response.data;
  },

  rejectLeaveRequest: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/reject`,
      { comments }
    );
    return response.data;
  },

  cancelLeaveRequest: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/cancel`,
      { comments }
    );
    return response.data;
  },

  requestLeaveCancellation: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/request-cancellation`,
      { comments }
    );
    return response.data;
  },

  // Attendance Locks
  getAttendanceLocks: async () => {
    const response = await api.get("/attendance-structure/attendance-locks");
    return response.data;
  },

  /**
   * GET /attendance-structure/my-attendance-history?month=1&year=2026
   * Returns attendance for the logged-in employee only (employee portal).
   * Same pattern as attendance-locks; use in employee attendance calendar.
   */
  getMyAttendanceHistory: async (month, year) => {
    const response = await api.get(
      "/attendance-structure/my-attendance-history",
      { params: { month, year } }
    );
    return response.data;
  },

  // Employee Policy Mappings
  getEmployeeWeeklyOffPolicies: async () => {
    const response = await api.get(
      "/attendance-structure/employee-weekly-off-policies"
    );
    return response.data;
  },

  getEmployeeHolidayPolicies: async () => {
    const response = await api.get(
      "/attendance-structure/employee-holiday-policies"
    );
    return response.data;
  },

  getEmployeeLeaveGroups: async () => {
    const response = await api.get(
      "/attendance-structure/employee-leave-groups"
    );
    return response.data;
  },
};

export const separationPolicyService = {
  getSeparationPolicies: async () => {
    const response = await api.get("/separation-policies");
    return response.data;
  },

  createSeparationPolicy: async (policyData) => {
    const response = await api.post("/separation-policies", policyData);
    return response.data;
  },

  updateSeparationPolicy: async (id, policyData) => {
    const response = await api.put(`/separation-policies/${id}`, policyData);
    return response.data;
  },

  deactivateSeparationPolicy: async (id) => {
    const response = await api.patch(`/separation-policies/${id}/deactivate`);
    return response.data;
  },

  getSeparationPolicyDeleteImpact: async (id) => {
    const response = await api.get(`/separation-policies/${id}/delete-impact`);
    return response.data;
  },

  deleteSeparationPolicy: async (id) => {
    const response = await api.delete(`/separation-policies/${id}`);
    return response.data;
  },
};

export const confirmationPolicyService = {
  getConfirmationPolicies: async () => {
    const response = await api.get("/confirmation-policies");
    return response.data;
  },
  createConfirmationPolicy: async (policyData) => {
    const response = await api.post("/confirmation-policies", policyData);
    return response.data;
  },
  updateConfirmationPolicy: async (id, policyData) => {
    const response = await api.put(`/confirmation-policies/${id}`, policyData);
    return response.data;
  },
  deactivateConfirmationPolicy: async (id) => {
    const response = await api.patch(`/confirmation-policies/${id}/deactivate`);
    return response.data;
  },
  getConfirmationPolicyDeleteImpact: async (id) => {
    const response = await api.get(`/confirmation-policies/${id}/delete-impact`);
    return response.data;
  },
  deleteConfirmationPolicy: async (id) => {
    const response = await api.delete(`/confirmation-policies/${id}`);
    return response.data;
  },
};

export const transferPolicyService = {
  getTransferPolicies: async () => {
    const response = await api.get("/transfer-policies");
    return response.data;
  },
  createTransferPolicy: async (policyData) => {
    const response = await api.post("/transfer-policies", policyData);
    return response.data;
  },
  updateTransferPolicy: async (id, policyData) => {
    const response = await api.put(`/transfer-policies/${id}`, policyData);
    return response.data;
  },
  deactivateTransferPolicy: async (id) => {
    const response = await api.patch(`/transfer-policies/${id}/deactivate`);
    return response.data;
  },
  getTransferPolicyDeleteImpact: async (id) => {
    const response = await api.get(`/transfer-policies/${id}/delete-impact`);
    return response.data;
  },
  deleteTransferPolicy: async (id) => {
    const response = await api.delete(`/transfer-policies/${id}`);
    return response.data;
  },
};

export const onboardingPolicyService = {
  getOnboardingPolicies: async () => {
    const response = await api.get("/onboarding-policies");
    return response.data;
  },

  createOnboardingPolicy: async (policyData) => {
    const response = await api.post("/onboarding-policies", policyData);
    return response.data;
  },

  updateOnboardingPolicy: async (id, policyData) => {
    const response = await api.put(`/onboarding-policies/${id}`, policyData);
    return response.data;
  },

  deactivateOnboardingPolicy: async (id) => {
    const response = await api.patch(`/onboarding-policies/${id}/deactivate`);
    return response.data;
  },

  getOnboardingPolicyDeleteImpact: async (id) => {
    const response = await api.get(`/onboarding-policies/${id}/delete-impact`);
    return response.data;
  },

  deleteOnboardingPolicy: async (id) => {
    const response = await api.delete(`/onboarding-policies/${id}`);
    return response.data;
  },
};

export const employeeOnboardingService = {
  listRequests: async (params = {}) => {
    const response = await api.get("/employee-onboarding", { params });
    return response.data;
  },

  listApprovalsDesk: async () => {
    const response = await api.get("/employee-onboarding/approvals-desk");
    return response.data;
  },

  listMyRequestsDesk: async () => {
    const response = await api.get("/employee-onboarding/my-requests-desk");
    return response.data;
  },

  listInsights: async () => {
    const response = await api.get("/employee-onboarding/insights");
    return response.data;
  },

  getRequest: async (id) => {
    const response = await api.get(`/employee-onboarding/${id}`);
    return response.data;
  },

  createRequest: async (payload) => {
    const response = await api.post("/employee-onboarding", payload);
    return response.data;
  },

  updateRequest: async (id, payload) => {
    const response = await api.patch(`/employee-onboarding/${id}`, payload);
    return response.data;
  },

  submitRequest: async (id, payload = {}) => {
    const response = await api.post(`/employee-onboarding/${id}/submit`, payload);
    return response.data;
  },

  approveRequest: async (id, comments, payload = {}) => {
    const response = await api.post(`/employee-onboarding/${id}/approve`, {
      comments,
      ...payload,
    });
    return response.data;
  },

  rejectRequest: async (id, comments) => {
    const response = await api.post(`/employee-onboarding/${id}/reject`, {
      comments,
    });
    return response.data;
  },

  completeChecklistTask: async (requestId, taskId, comments) => {
    const response = await api.post(
      `/employee-onboarding/${requestId}/checklist/${taskId}/complete`,
      { comments, reason: comments }
    );
    return response.data;
  },

  sendBackChecklistTask: async (requestId, taskId, comments) => {
    const response = await api.post(
      `/employee-onboarding/${requestId}/checklist/${taskId}/reject`,
      { comments, reason: comments }
    );
    return response.data;
  },

  rejectChecklistTask: async (requestId, taskId, comments) => {
    const response = await api.post(
      `/employee-onboarding/${requestId}/checklist/${taskId}/reject`,
      { comments, reason: comments }
    );
    return response.data;
  },
};

// Error handling wrapper
const handleApiError = (error) => {
  // If the error has a detailed response object from our server,
  // it's a validation error. Throw the ORIGINAL error so the component can see the details.
  if (error.response && error.response.data) {
    throw error;
  }

  // For any other kind of error (like a network failure), create a simple message.
  const errorMessage = error.message || "An unexpected network error occurred.";
  throw new Error(errorMessage);
};

// Add error handling to all service methods
export const homeService = {
  getHighlights: async (params = {}) => {
    const response = await api.get("/home/highlights", { params });
    return response.data;
  },
  sendCelebration: async (data) => {
    const response = await api.post("/home/celebrations", data);
    return response.data;
  },
  listNotifications: async () => {
    const response = await api.get("/home/notifications");
    return response.data;
  },
  dismissNotification: async (id) => {
    const response = await api.post(`/home/notifications/${id}/dismiss`);
    return response.data;
  },
  clearNotifications: async () => {
    const response = await api.post("/home/notifications/clear");
    return response.data;
  },
};

export const activityLogService = {
  getMeta: async () => {
    const response = await api.get("/activity-logs/meta");
    return response.data;
  },
  list: async (params = {}) => {
    const response = await api.get("/activity-logs", { params });
    return response.data;
  },
  listArchives: async (params = {}) => {
    const response = await api.get("/activity-logs/archives", { params });
    return response.data;
  },
  getArchiveRows: async (id, params = {}) => {
    const response = await api.get(`/activity-logs/archives/${id}`, { params });
    return response.data;
  },
  exportExcel: async (params = {}) => {
    const response = await api.get("/activity-logs/export", {
      params,
      responseType: "blob",
    });
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-log-${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
  runMaintenance: async () => {
    const response = await api.post("/activity-logs/maintenance");
    return response.data;
  },
};

export const notificationSettingsService = {
  getCatalog: async () => {
    const response = await api.get("/notification-settings/catalog");
    return response.data;
  },
  list: async () => {
    const response = await api.get("/notification-settings");
    return response.data;
  },
  saveProduct: async (triggerKey, payload) => {
    const response = await api.put(
      `/notification-settings/product/${encodeURIComponent(triggerKey)}`,
      payload
    );
    return response.data;
  },
  createCustom: async (payload) => {
    const response = await api.post("/notification-settings/custom", payload);
    return response.data;
  },
  updateCustom: async (id, payload) => {
    const response = await api.put(`/notification-settings/custom/${id}`, payload);
    return response.data;
  },
  removeCustom: async (id) => {
    const response = await api.delete(`/notification-settings/custom/${id}`);
    return response.data;
  },
  preview: async (payload) => {
    const response = await api.post("/notification-settings/preview", payload);
    return response.data;
  },
  sendTest: async (payload) => {
    const response = await api.post("/notification-settings/send-test", payload);
    return response.data;
  },
};

export const hiringService = {
  getMeta: async () => (await api.get("/hiring/meta")).data,
  getConfig: async () => (await api.get("/hiring/config")).data,
  saveConfig: async (config) =>
    (await api.put("/hiring/config", { config })).data,
  getDashboard: async () => (await api.get("/hiring/dashboard")).data,
  listPolicies: async () => (await api.get("/hiring/policies")).data,
  createPolicy: async (payload) =>
    (await api.post("/hiring/policies", payload)).data,
  updatePolicy: async (id, payload) =>
    (await api.put(`/hiring/policies/${id}`, payload)).data,
  deletePolicy: async (id) =>
    (await api.delete(`/hiring/policies/${id}`)).data,
  listMrfs: async (params = {}) =>
    (await api.get("/hiring/mrfs", { params })).data,
  createMrf: async (payload) => (await api.post("/hiring/mrfs", payload)).data,
  approveMrf: async (id) =>
    (await api.post(`/hiring/mrfs/${id}/approve`)).data,
  rejectMrf: async (id, reason) =>
    (await api.post(`/hiring/mrfs/${id}/reject`, { reason })).data,
  listJobs: async (params = {}) =>
    (await api.get("/hiring/jobs", { params })).data,
  listCandidates: async () => (await api.get("/hiring/candidates")).data,
  createCandidate: async (payload) =>
    (await api.post("/hiring/candidates", payload)).data,
  resendInvite: async (id) =>
    (await api.post(`/hiring/candidates/${id}/resend-invite`)).data,
  listApplications: async () => (await api.get("/hiring/applications")).data,
  submitApplication: async (payload) =>
    (await api.post("/hiring/applications", payload)).data,
  updateStage: async (id, payload) =>
    (await api.patch(`/hiring/applications/${id}/stage`, payload)).data,
  getPublicProfile: async (token) =>
    (await api.get(`/hiring/public/profile/${encodeURIComponent(token)}`)).data,
  savePublicProfile: async (token, payload) =>
    (await api.put(`/hiring/public/profile/${encodeURIComponent(token)}`, payload))
      .data,
};

Object.values([
  employeeService,
  attendanceService,
  payrollService,
  branchService,
  departmentService,
  designationService,
  subDepartmentService,
  gradesService,
  categoryService,
  reasonsService,
  employeeCodeStructureService,
  confirmationService,
  employeeConfirmationService,
  employeeTransferService,
  employeeSeparationService,
  transferService,
  salaryStructureService,
  flexiBenefitsService,
  statutoryReportsService,
  salaryService,
  payrollOpsService,
  resignationService,
  letterTemplateService,
  issuedLetterService,
  attendanceStructureService,
  homeService,
  activityLogService,
  notificationSettingsService,
  hiringService,
  assetService,
]).forEach((service) => {
  Object.keys(service).forEach((key) => {
    const originalMethod = service[key];
    service[key] = async (...args) => {
      try {
        return await originalMethod(...args);
      } catch (error) {
        handleApiError(error);
      }
    };
  });
});

export default api;
